// Biology → Section 2: Humans as Organisms (KS3, sourced from the
// Grade9Ready science PDF). Each topic follows the rich-content template
// established in biologySection1.js and the Digestion topic in this file.
// Authored topics from the PDF pipeline (`authoredTopics.json`) replace
// any topic with a matching id at runtime.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'biology/humans-as-organisms';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 2.1  NUTRITION
// =====================================================================
const NUTRITION_TOPIC = {
  id: 'bio-2.1',
  title: 'Nutrition',
  slug: 'nutrition',
  pageRef: 'Section 2 · Topic 1',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'The seven nutrients',
        body:
          'A balanced diet contains seven types of nutrient: carbohydrates, proteins, fats (lipids), fibre, vitamins, minerals and water. Each plays a different role. Carbs give us quick energy; protein builds and repairs cells; fat stores energy and protects organs; fibre keeps the gut moving; vitamins and minerals help reactions in tiny amounts; and water is the solvent for everything in the body.',
        check: {
          question: 'Which nutrient gives the most energy per gram?',
          options: ['Carbohydrate', 'Protein', 'Fat', 'Water'],
          answer: 'Fat',
          explainer: 'Fat releases roughly 9 kcal per gram — more than double carbs (~4 kcal) or protein (~4 kcal).',
        },
      },
      {
        heading: 'Why each nutrient matters',
        body:
          'Protein is needed for growth, repair and making enzymes. Fat is a long-term energy store and insulates the body. Carbohydrates (especially starches and sugars) provide the fuel for respiration. Fibre cannot be digested but adds bulk so muscles can push food through the gut. Vitamins (e.g. C for healthy skin) and minerals (e.g. iron for blood, calcium for bones) are needed only in small amounts but are essential.',
        check: {
          question: 'Lack of which mineral makes red blood cells unable to carry enough oxygen?',
          options: ['Calcium', 'Sodium', 'Iron', 'Potassium'],
          answer: 'Iron',
          explainer: 'Iron is a key part of haemoglobin, the protein that carries oxygen in red blood cells.',
        },
      },
      {
        heading: 'Balanced diet and deficiency',
        body:
          'A balanced diet has all seven nutrients in the right proportions for your age, sex and activity level. Eating too little protein can lead to wasted muscles (kwashiorkor); too little vitamin C causes scurvy; too little iron causes anaemia. Eating too much energy-rich food without enough exercise can cause obesity, raising the risk of type-2 diabetes and heart disease.',
        check: {
          question: 'Scurvy is caused by a lack of…',
          options: ['Iron', 'Vitamin C', 'Calcium', 'Fibre'],
          answer: 'Vitamin C',
          explainer: 'Vitamin C is needed to make collagen for skin and gums. Sailors used to get scurvy on long voyages without fresh fruit.',
        },
      },
    ],
    keyPoints: [
      'Seven nutrients: carbs, protein, fat, fibre, vitamins, minerals, water',
      'Carbs/fats give energy; protein builds and repairs',
      'Fat = ~9 kcal/g, carbs/protein = ~4 kcal/g',
      'Iron deficiency → anaemia; vitamin C deficiency → scurvy',
      'Fibre is indigestible but keeps food moving through the gut',
      'Too much energy + low activity → obesity (and related diseases)',
    ],
    workedExamples: [
      {
        problem: 'A 100 g cereal bar contains 60 g carbohydrate, 5 g protein and 10 g fat. Calculate the total energy in kcal.',
        steps: [
          { step: 1, instruction: 'Energy from carbs', working: '60 × 4 = 240 kcal' },
          { step: 2, instruction: 'Energy from protein', working: '5 × 4 = 20 kcal' },
          { step: 3, instruction: 'Energy from fat', working: '10 × 9 = 90 kcal' },
          { step: 4, instruction: 'Add them up', working: '240 + 20 + 90 = 350 kcal' },
        ],
        answer: 'Total energy = 350 kcal',
        commonMistake: 'Forgetting that fat releases more than twice as much energy per gram as carbs/protein.',
      },
    ],
    whyItWorks:
      'Different nutrients are absorbed by different parts of the gut. Sugars and amino acids cross the small intestine wall directly into the blood; fats end up in the lymph; water is reabsorbed in the large intestine. The body then uses or stores them depending on need. Eat the right balance and every system has what it needs to work well.',
  },
  glossary: [
    { term: 'Nutrient', definition: 'A substance from food that the body needs for energy, growth or repair.' },
    { term: 'Carbohydrate', definition: 'A nutrient (sugars and starches) used as the body\u2019s main energy source.' },
    { term: 'Protein', definition: 'A nutrient used for growth, repair and making enzymes.' },
    { term: 'Fat (lipid)', definition: 'A nutrient that gives the most energy per gram and acts as long-term storage.' },
    { term: 'Fibre', definition: 'Indigestible plant material that keeps food moving through the gut.' },
    { term: 'Vitamin', definition: 'An organic substance needed in small amounts for health (e.g. vitamin C).' },
    { term: 'Mineral', definition: 'An inorganic substance needed in small amounts (e.g. iron, calcium).' },
    { term: 'Deficiency', definition: 'A health problem caused by not having enough of a particular nutrient.' },
  ],
  examQuestion: {
    prompt:
      'A student\u2019s breakfast contains 50 g carbohydrate, 8 g protein and 15 g fat. (a) Calculate the total energy in kcal. (b) Explain why a balanced diet must also contain iron and vitamin C, even though they are needed only in tiny amounts. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Carbs energy 50 × 4 = 200 kcal',
      'Protein energy 8 × 4 = 32; Fat energy 15 × 9 = 135 kcal; total ≈ 367 kcal',
      'Iron needed for haemoglobin / preventing anaemia',
      'Vitamin C needed for healthy skin/gums / preventing scurvy',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.1.w1', type: 'text', question: 'How many types of nutrient are there?', answer: '7', options: [], hint1: 'Less than 10', hint2: 'Single digit', hint3: 'Lucky number', altAnswers: ['seven'] },
      { id: 'bio-2.1.w2', type: 'choice', question: 'Which nutrient is the body\u2019s main energy source?', options: ['Protein', 'Carbohydrate', 'Vitamin', 'Mineral'], answer: 'Carbohydrate', hint1: 'Sugars and starches', hint2: 'Bread, rice, pasta', hint3: '12 letters' },
      { id: 'bio-2.1.w3', type: 'choice', question: 'Which nutrient is needed for growth and repair?', options: ['Carbohydrate', 'Fat', 'Protein', 'Fibre'], answer: 'Protein', hint1: 'Made of amino acids', hint2: 'Meat, eggs, beans', hint3: '7 letters' },
      { id: 'bio-2.1.w4', type: 'text', question: 'Which mineral helps red blood cells carry oxygen?', answer: 'iron', options: [], hint1: '4 letters', hint2: 'A metal', hint3: 'Found in red meat' },
    ],
    level1: [
      { id: 'bio-2.1.1', type: 'choice', question: 'Lack of vitamin C causes…', options: ['Anaemia', 'Scurvy', 'Rickets', 'Obesity'], answer: 'Scurvy', hint1: 'Sailors got it', hint2: 'Bleeding gums', hint3: '6 letters', options: ['Anaemia', 'Scurvy', 'Rickets', 'Obesity'] },
      { id: 'bio-2.1.2', type: 'text', question: 'How many kcal does 1 g of fat release?', answer: '9', options: [], hint1: 'More than carbs', hint2: 'Single digit', hint3: 'Just below 10' },
      { id: 'bio-2.1.3', type: 'choice', question: 'Which nutrient adds bulk to push food through the gut?', options: ['Protein', 'Vitamin', 'Fibre', 'Water'], answer: 'Fibre', hint1: 'Cannot be digested', hint2: 'Found in wholegrains and veg', hint3: '5 letters' },
      { id: 'bio-2.1.4', type: 'choice', question: 'Calcium is mainly used for…', options: ['Carrying oxygen', 'Building strong bones and teeth', 'Making enzymes', 'Storing energy'], answer: 'Building strong bones and teeth', hint1: 'Found in dairy', hint2: 'Bones', hint3: 'Not a vitamin' },
    ],
    level2: [
      { id: 'bio-2.1.5', type: 'number', question: 'A bag of crisps has 30 g carbs, 2 g protein, 12 g fat. Total kcal?', answer: '236', options: [], hint1: '(30×4) + (2×4) + (12×9)', hint2: '120 + 8 + 108', hint3: 'Three digits' },
      { id: 'bio-2.1.6', type: 'text', question: 'Iron deficiency causes a condition called ___ .', answer: 'anaemia', options: [], hint1: 'Starts with a', hint2: '7 letters', hint3: 'Tiredness from low blood oxygen' },
      { id: 'bio-2.1.7', type: 'choice', question: 'Eating more energy than you use over time leads to…', options: ['Anaemia', 'Scurvy', 'Obesity', 'Dehydration'], answer: 'Obesity', hint1: 'Long-term effect', hint2: 'Stored as fat', hint3: '7 letters' },
    ],
    level3: [
      { id: 'bio-2.1.8', type: 'choice', question: 'Why is water classed as a nutrient even though it provides no energy?', options: [
        'It tastes good',
        'It is the solvent for chemical reactions in cells',
        'It contains protein',
        'It can replace fat',
      ], answer: 'It is the solvent for chemical reactions in cells', hint1: 'Around 60% of body mass', hint2: 'Reactions need it', hint3: 'Plasma is mostly water' },
      { id: 'bio-2.1.9', type: 'text', question: 'Diets too rich in salt raise the risk of which condition?', answer: 'high blood pressure', options: [], hint1: 'Cardiovascular issue', hint2: 'Three words', hint3: 'Hypertension', altAnswers: ['hypertension', 'heart disease'] },
    ],
  },
};

// =====================================================================
// 2.3  THE SKELETON
// =====================================================================
const SKELETON_TOPIC = {
  id: 'bio-2.3',
  title: 'The Skeleton',
  slug: 'the-skeleton',
  pageRef: 'Section 2 · Topic 3',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Four jobs of the skeleton',
        body:
          'Your skeleton has roughly 206 bones and does four big jobs. (1) Support — without it, your body would collapse. (2) Movement — bones act as levers when muscles pull on them. (3) Protection — the skull guards the brain, the ribs the heart and lungs. (4) Making blood cells — red and white cells are produced inside the bone marrow.',
        check: {
          question: 'Which of these is NOT a job of the skeleton?',
          options: ['Support', 'Protection', 'Digesting food', 'Making blood cells'],
          answer: 'Digesting food',
          explainer: 'Digestion is the job of the gut, not the skeleton.',
        },
      },
      {
        heading: 'Bones, joints and movement',
        body:
          'Where two bones meet is called a joint. There are three main types: fixed joints (e.g. between skull bones — no movement); hinge joints (elbow, knee — movement in one direction like a door); and ball-and-socket joints (shoulder, hip — movement in nearly every direction). Cartilage cushions the ends of bones, and a slippery synovial fluid lets them slide.',
        check: {
          question: 'Which joint type is found at the shoulder?',
          options: ['Fixed', 'Hinge', 'Ball-and-socket', 'Pivot'],
          answer: 'Ball-and-socket',
          explainer: 'A ball-and-socket lets the arm rotate in nearly every direction.',
        },
      },
      {
        heading: 'Why bones don\u2019t snap',
        body:
          'Bones look solid but are alive — built from calcium phosphate (for hardness) and collagen fibres (for flexibility). The combination makes bone strong but not brittle. If calcium runs low, bones become weak (rickets in children, osteoporosis in older adults). Weight-bearing exercise helps keep bones strong.',
        check: {
          question: 'Bones are strong because they contain…',
          options: ['Iron and water', 'Calcium and collagen', 'Fat and fibre', 'Salt only'],
          answer: 'Calcium and collagen',
          explainer: 'Calcium gives hardness, collagen gives flexibility — together they resist snapping.',
        },
      },
    ],
    keyPoints: [
      'Skeleton: support, movement, protection, blood cell production',
      '206 bones in adults',
      'Three joint types: fixed, hinge, ball-and-socket',
      'Cartilage cushions ends of bones; synovial fluid lubricates',
      'Bone = calcium phosphate + collagen — strong AND flexible',
      'Low calcium → rickets / osteoporosis',
    ],
    workedExamples: [
      {
        problem: 'Match each joint to its movement: knee, hip, between skull bones.',
        steps: [
          { step: 1, instruction: 'Knee — what kind of motion?', working: 'Bends in one direction → hinge' },
          { step: 2, instruction: 'Hip — what kind of motion?', working: 'Rotates many directions → ball-and-socket' },
          { step: 3, instruction: 'Skull joints — what kind?', working: 'No movement → fixed' },
        ],
        answer: 'Knee = hinge, hip = ball-and-socket, skull = fixed.',
        commonMistake: 'Calling the knee a ball-and-socket. The knee only bends one way.',
      },
    ],
    whyItWorks:
      'The skeleton is not just a frame — it grows, repairs and even produces blood cells in the marrow. Its design balances rigidity (from calcium) with flexibility (from collagen). Joints are precisely engineered: hinges for power and stability, ball-and-sockets for range. That\u2019s why a footballer can sprint AND swivel at speed.',
  },
  glossary: [
    { term: 'Skeleton', definition: 'The framework of bones that supports the body.' },
    { term: 'Bone', definition: 'Hard living tissue made of calcium phosphate and collagen fibres.' },
    { term: 'Joint', definition: 'Where two or more bones meet.' },
    { term: 'Hinge joint', definition: 'A joint that bends in one direction (knee, elbow).' },
    { term: 'Ball-and-socket joint', definition: 'A joint that allows movement in many directions (shoulder, hip).' },
    { term: 'Fixed joint', definition: 'A joint with no movement (between skull bones).' },
    { term: 'Cartilage', definition: 'Tough, smooth tissue that cushions the ends of bones.' },
    { term: 'Synovial fluid', definition: 'Slippery liquid that lubricates a joint.' },
    { term: 'Bone marrow', definition: 'Soft tissue inside bones where blood cells are made.' },
  ],
  examQuestion: {
    prompt:
      'Describe the four main functions of the human skeleton. Explain why the elbow uses a hinge joint while the shoulder uses a ball-and-socket joint. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Functions: support, movement, protection, blood cell production',
      'Hinge joint = movement in one direction (forearm straightens/bends)',
      'Ball-and-socket = rotation in many directions',
      'Different joints suit different functions / examples (e.g. throwing requires shoulder rotation)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.3.w1', type: 'text', question: 'Approximate number of bones in an adult human?', answer: '206', options: [], hint1: 'Around 200', hint2: 'Even number', hint3: 'Just over 200' },
      { id: 'bio-2.3.w2', type: 'choice', question: 'Which protects the brain?', options: ['Ribs', 'Skull', 'Pelvis', 'Spine'], answer: 'Skull', hint1: 'Top of body', hint2: 'Cranial bone', hint3: '5 letters' },
      { id: 'bio-2.3.w3', type: 'choice', question: 'The knee is a ___ joint.', options: ['Hinge', 'Ball-and-socket', 'Fixed', 'Pivot'], answer: 'Hinge', hint1: 'Bends one way', hint2: 'Like a door', hint3: '5 letters' },
      { id: 'bio-2.3.w4', type: 'text', question: 'Bones are made strong by which mineral?', answer: 'calcium', options: [], hint1: 'Found in milk', hint2: '7 letters', hint3: 'Symbol Ca' },
    ],
    level1: [
      { id: 'bio-2.3.1', type: 'choice', question: 'Where are blood cells produced?', options: ['Cartilage', 'Bone marrow', 'Skin', 'Liver'], answer: 'Bone marrow', hint1: 'Inside bones', hint2: 'Two words', hint3: 'Soft tissue' },
      { id: 'bio-2.3.2', type: 'text', question: 'The hip joint is a ___ -and-socket joint.', answer: 'ball', options: [], hint1: '4 letters', hint2: 'Round end', hint3: 'Sits in a hollow' },
      { id: 'bio-2.3.3', type: 'choice', question: 'Synovial fluid is found in joints to…', options: ['Make bones grow', 'Lubricate them', 'Carry oxygen', 'Store fat'], answer: 'Lubricate them', hint1: 'Reduces friction', hint2: 'Like oil', hint3: '9 letters' },
    ],
    level2: [
      { id: 'bio-2.3.4', type: 'choice', question: 'Which combination makes bone strong AND flexible?', options: [
        'Calcium and water',
        'Calcium and collagen',
        'Iron and protein',
        'Cartilage and salt',
      ], answer: 'Calcium and collagen', hint1: 'Hard + bendy', hint2: 'Two-part composite', hint3: 'Mineral + protein' },
      { id: 'bio-2.3.5', type: 'choice', question: 'A weakened, brittle bone disease in older adults is called…', options: ['Anaemia', 'Osteoporosis', 'Scurvy', 'Asthma'], answer: 'Osteoporosis', hint1: 'Loss of calcium', hint2: 'Common in over-60s', hint3: '12 letters' },
    ],
    level3: [
      { id: 'bio-2.3.6', type: 'choice', question: 'Why are babies\u2019 skeletons partly made of cartilage?', options: [
        'Cartilage is hard',
        'Cartilage is more flexible — easier to be born and grow',
        'It carries oxygen',
        'Cartilage is heavier',
      ], answer: 'Cartilage is more flexible — easier to be born and grow', hint1: 'Not yet fully ossified', hint2: 'Allows growth', hint3: 'Becomes bone over time' },
      { id: 'bio-2.3.7', type: 'text', question: 'Cartilage hardens into bone in a process called ___.', answer: 'ossification', options: [], hint1: 'Starts with o', hint2: '12 letters', hint3: 'Latin os = bone' },
    ],
  },
};

// =====================================================================
// 2.4  THE MUSCULAR SYSTEM
// =====================================================================
const MUSCULAR_TOPIC = {
  id: 'bio-2.4',
  title: 'The Muscular System',
  slug: 'muscular-system',
  pageRef: 'Section 2 · Topic 4',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Three types of muscle',
        body:
          'Your body has three muscle types. Skeletal muscle (e.g. biceps) attaches to bone, moves the body, and is under your conscious control. Smooth muscle lines the gut and blood vessels — it works automatically (you can\u2019t choose to digest food). Cardiac muscle is found only in the heart and never tires.',
        check: {
          question: 'Which muscle type is found ONLY in the heart?',
          options: ['Skeletal', 'Smooth', 'Cardiac', 'Striated'],
          answer: 'Cardiac',
          explainer: 'Cardiac muscle is unique to the heart and works without you thinking about it.',
        },
      },
      {
        heading: 'How muscles move bones',
        body:
          'Muscles can only PULL — they cannot push. To move a bone in two directions you need two muscles working as an antagonistic pair. In the upper arm, the biceps and triceps work this way: biceps contracts to bend (flex) the arm; triceps contracts to straighten (extend) it. Tendons connect muscle to bone; ligaments connect bone to bone.',
        check: {
          question: 'When the arm bends at the elbow, which muscle CONTRACTS?',
          options: ['Triceps', 'Biceps', 'Both at the same time', 'Neither'],
          answer: 'Biceps',
          explainer: 'The biceps shortens (contracts) to bend the arm; the triceps relaxes.',
        },
      },
      {
        heading: 'Energy and fatigue',
        body:
          'Muscle cells need lots of energy. They get it from glucose using oxygen — aerobic respiration. During hard exercise oxygen can\u2019t arrive fast enough, so muscles switch to anaerobic respiration, producing lactic acid. Lactic acid build-up causes that burning, fatigued feeling. After exercise you breathe heavily to repay the oxygen debt and break down the lactic acid.',
        check: {
          question: 'Anaerobic respiration in muscles produces…',
          options: ['Carbon dioxide and water', 'Lactic acid', 'Oxygen', 'Sugar'],
          answer: 'Lactic acid',
          explainer: 'Without enough oxygen, glucose breaks down to lactic acid, which causes the burning feeling.',
        },
      },
    ],
    keyPoints: [
      'Three muscle types: skeletal, smooth, cardiac',
      'Muscles can only PULL — never push',
      'Antagonistic pairs (biceps/triceps) move bones in two directions',
      'Tendons connect muscle to bone; ligaments connect bone to bone',
      'Aerobic respiration with oxygen; anaerobic without — produces lactic acid',
      'Heavy breathing after exercise repays the oxygen debt',
    ],
    workedExamples: [
      {
        problem: 'A student lifts a dumbbell, then lowers it slowly. Describe what happens to the biceps and triceps.',
        steps: [
          { step: 1, instruction: 'Lifting the dumbbell — arm bends', working: 'Biceps contracts; triceps relaxes' },
          { step: 2, instruction: 'Lowering it slowly — arm straightens', working: 'Triceps contracts; biceps relaxes (controlled)' },
          { step: 3, instruction: 'State the relationship', working: 'Biceps and triceps form an antagonistic pair' },
        ],
        answer: 'They are an antagonistic pair: biceps contracts to lift, triceps contracts to lower; only one is contracting at a time.',
        commonMistake: 'Saying both contract together. They alternate — when one shortens, the other relaxes.',
      },
    ],
    whyItWorks:
      'Muscles work as opposing teams. Because a muscle can only pull, every reversible movement needs a partner muscle to pull the bone the other way. This explains why most movements involve flexor + extensor muscles, and why injuring a tendon (which transmits the pull) means the joint can\u2019t move properly.',
  },
  glossary: [
    { term: 'Skeletal muscle', definition: 'Muscle attached to bones; under conscious control.' },
    { term: 'Smooth muscle', definition: 'Muscle that lines internal organs (e.g. gut) and works automatically.' },
    { term: 'Cardiac muscle', definition: 'Muscle found only in the heart that contracts continuously.' },
    { term: 'Antagonistic pair', definition: 'Two muscles that work in opposite directions across a joint.' },
    { term: 'Tendon', definition: 'Tough, inelastic tissue connecting muscle to bone.' },
    { term: 'Ligament', definition: 'Tough elastic tissue connecting bone to bone, holding joints together.' },
    { term: 'Aerobic respiration', definition: 'Releasing energy from glucose using oxygen.' },
    { term: 'Anaerobic respiration', definition: 'Releasing energy from glucose without oxygen — produces lactic acid in muscles.' },
    { term: 'Oxygen debt', definition: 'The extra oxygen needed after exercise to break down lactic acid.' },
  ],
  examQuestion: {
    prompt:
      'Explain how the biceps and triceps work as an antagonistic pair to bend and straighten the arm. Why does the body need pairs of muscles like this rather than just one? (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Biceps contracts (shortens) to bend / flex the arm',
      'Triceps contracts to straighten / extend the arm',
      'They alternate — only one contracts at a time',
      'Muscles can only pull (not push), so a partner is needed for the opposite movement',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.4.w1', type: 'text', question: 'Tendons join muscle to ___ .', answer: 'bone', options: [], hint1: '4 letters', hint2: 'Skeleton', hint3: 'Hard tissue' },
      { id: 'bio-2.4.w2', type: 'text', question: 'Ligaments join bone to ___ .', answer: 'bone', options: [], hint1: '4 letters', hint2: 'Same word as above', hint3: 'They hold joints' },
      { id: 'bio-2.4.w3', type: 'choice', question: 'Muscles can only…', options: ['Push', 'Pull', 'Stretch', 'Snap'], answer: 'Pull', hint1: 'Contraction shortens', hint2: '4 letters', hint3: 'Towards the body' },
      { id: 'bio-2.4.w4', type: 'choice', question: 'The biceps bend the arm; the triceps…', options: ['Bend it more', 'Straighten it', 'Rotate it', 'Lift the leg'], answer: 'Straighten it', hint1: 'Opposite of bend', hint2: 'Antagonistic pair', hint3: 'Extension' },
    ],
    level1: [
      { id: 'bio-2.4.1', type: 'choice', question: 'Which muscle type is involuntary AND found in the gut?', options: ['Skeletal', 'Cardiac', 'Smooth', 'Striated'], answer: 'Smooth', hint1: 'Not under conscious control', hint2: '6 letters', hint3: 'Lines blood vessels too' },
      { id: 'bio-2.4.2', type: 'choice', question: 'Lactic acid is made when muscles…', options: [
        'Have plenty of oxygen',
        'Run low on oxygen during hard exercise',
        'Are at rest',
        'Are growing',
      ], answer: 'Run low on oxygen during hard exercise', hint1: 'Anaerobic respiration', hint2: 'Causes burning sensation', hint3: 'Sprinting' },
      { id: 'bio-2.4.3', type: 'text', question: 'A pair of muscles working in opposite directions is called an ___ pair.', answer: 'antagonistic', options: [], hint1: '12 letters', hint2: 'Starts with a', hint3: 'Latin: against-acting' },
    ],
    level2: [
      { id: 'bio-2.4.4', type: 'choice', question: 'After hard exercise, why do you continue to breathe heavily?', options: [
        'To take in more food',
        'To repay the oxygen debt and break down lactic acid',
        'To cool the body down',
        'To digest faster',
      ], answer: 'To repay the oxygen debt and break down lactic acid', hint1: 'Recovery process', hint2: 'Lactic acid clearance', hint3: 'Oxygen debt' },
      { id: 'bio-2.4.5', type: 'text', question: 'Cardiac muscle is found ONLY in the ___ .', answer: 'heart', options: [], hint1: '5 letters', hint2: 'Pumps blood', hint3: 'Beats your whole life' },
    ],
    level3: [
      { id: 'bio-2.4.6', type: 'choice', question: 'A torn ACL is a damaged ligament in the knee. What problem would this cause?', options: [
        'The bones cannot grow',
        'The joint becomes unstable and bones can move incorrectly',
        'The biceps stops working',
        'The arm cannot bend',
      ], answer: 'The joint becomes unstable and bones can move incorrectly', hint1: 'Ligaments stabilise joints', hint2: 'Bone-to-bone link', hint3: 'Common football injury' },
      { id: 'bio-2.4.7', type: 'choice', question: 'Why is cardiac muscle special?', options: [
        'It tires after a few minutes',
        'It contracts continuously without conscious control and rarely fatigues',
        'It is only used during exercise',
        'It is voluntary',
      ], answer: 'It contracts continuously without conscious control and rarely fatigues', hint1: 'Beats every second', hint2: 'No rest', hint3: 'Only in heart' },
    ],
  },
};

// =====================================================================
// 2.5  FORCE APPLIED BY MUSCLES
// =====================================================================
const FORCE_TOPIC = {
  id: 'bio-2.5',
  title: 'Force Applied by Muscles',
  slug: 'force-applied-by-muscles',
  pageRef: 'Section 2 · Topic 5',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Bones as levers',
        body:
          'When a muscle pulls on a bone, the bone acts as a lever, with the joint as the pivot. Three things matter: the EFFORT (the force the muscle provides), the LOAD (the weight being moved), and the distance from each to the pivot. The forearm is a typical example — the elbow is the pivot, the biceps is the effort, and a held weight is the load.',
        check: {
          question: 'In the forearm, what acts as the pivot?',
          options: ['The biceps', 'The elbow joint', 'The hand', 'The shoulder'],
          answer: 'The elbow joint',
          explainer: 'The elbow is the fixed point that the bone rotates around.',
        },
      },
      {
        heading: 'Moments — turning forces',
        body:
          'A turning force is called a moment. Moment = force × distance from the pivot. The unit is newton metres (N m). A bigger force, OR a longer distance, gives a bigger moment. That\u2019s why a long spanner makes loosening a tight bolt easier — same force, longer lever arm, larger moment.',
        check: {
          question: 'Moment = force × ___ .',
          options: ['Time', 'Mass', 'Distance from the pivot', 'Speed'],
          answer: 'Distance from the pivot',
          explainer: 'Moments depend on perpendicular distance from the pivot — the farther the force, the bigger the turning effect.',
        },
      },
      {
        heading: 'Why muscles use BIG forces',
        body:
          'In the human arm, the biceps attaches very close to the elbow. The load (e.g. a dumbbell in the hand) is much further from the pivot. To lift even a 5 kg weight, the biceps must apply a much larger force — often 5 to 7 times bigger. The advantage is speed and range of movement: a small contraction of the biceps moves the hand a long distance.',
        check: {
          question: 'Why must the biceps apply more force than the load it lifts?',
          options: [
            'Because it is short',
            'Because it attaches close to the pivot, with a smaller distance than the load',
            'Because gravity helps',
            'Because the load is hot',
          ],
          answer: 'Because it attaches close to the pivot, with a smaller distance than the load',
          explainer: 'A smaller distance means a smaller moment per newton — so more newtons are needed.',
        },
      },
    ],
    keyPoints: [
      'Bones = levers; joints = pivots; muscles = effort',
      'Moment = force × distance from pivot (units: N m)',
      'Bigger distance gives bigger moment for the same force',
      'Muscles attach close to the pivot, so they need to apply LARGE forces',
      'Trade-off: large muscle force → small movement, but the hand moves much further/faster',
    ],
    workedExamples: [
      {
        problem: 'A 5 N weight is held in the hand 30 cm from the elbow. The biceps attaches 5 cm from the elbow. What force must the biceps apply to balance the weight?',
        steps: [
          { step: 1, instruction: 'Convert distances to metres', working: '30 cm = 0.30 m, 5 cm = 0.05 m' },
          { step: 2, instruction: 'Calculate load moment', working: 'Moment = 5 × 0.30 = 1.5 N m' },
          { step: 3, instruction: 'Set biceps moment equal to load moment', working: 'Biceps force × 0.05 = 1.5' },
          { step: 4, instruction: 'Solve for biceps force', working: 'Force = 1.5 ÷ 0.05 = 30 N' },
        ],
        answer: 'Biceps force = 30 N (six times the load).',
        commonMistake: 'Forgetting to convert cm to m — answer would still be numerically right BUT inconsistent with other parts of a question.',
      },
      {
        problem: 'A spanner is used to undo a wheel nut. The student pushes with 50 N at 25 cm from the nut. Calculate the moment.',
        steps: [
          { step: 1, instruction: 'Convert distance', working: '25 cm = 0.25 m' },
          { step: 2, instruction: 'Apply formula', working: 'Moment = 50 × 0.25' },
          { step: 3, instruction: 'Calculate', working: '= 12.5 N m' },
        ],
        answer: 'Moment = 12.5 N m.',
        commonMistake: 'Using the wrong distance unit. Always convert cm to m first.',
      },
    ],
    whyItWorks:
      'The forearm is a third-class lever — effort between pivot and load. It loses out on force (the biceps must work hard) but wins on speed and range. That\u2019s why a small biceps shortening of a few centimetres moves the hand a much greater distance — perfect for throwing, writing or playing sport.',
  },
  glossary: [
    { term: 'Lever', definition: 'A rigid bar that rotates around a pivot when a force is applied.' },
    { term: 'Pivot', definition: 'The fixed point a lever rotates around. In the arm, the elbow joint.' },
    { term: 'Effort', definition: 'The force applied to the lever (by the muscle).' },
    { term: 'Load', definition: 'The weight being moved by the lever.' },
    { term: 'Moment', definition: 'A turning force, calculated by force × distance from pivot.' },
    { term: 'Newton metre (N m)', definition: 'The unit of moment — newtons multiplied by metres.' },
    { term: 'Equilibrium', definition: 'When clockwise and anticlockwise moments balance and the lever does not rotate.' },
  ],
  examQuestion: {
    prompt:
      'A student holds a 6 N dumbbell 28 cm from her elbow. Her biceps attaches 4 cm from the elbow. (a) Calculate the moment of the dumbbell. (b) Calculate the force her biceps must apply to hold the dumbbell still. (c) Explain why the biceps force is so much larger than the dumbbell weight. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Dumbbell moment = 6 × 0.28 = 1.68 N m',
      'Biceps force = 1.68 ÷ 0.04 = 42 N',
      'Biceps attaches close to the pivot, so a small distance × large force = same moment',
      'Trade-off: small muscle movement gives large hand movement',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.5.w1', type: 'text', question: 'Bones act as ___ when a muscle pulls them.', answer: 'levers', options: [], hint1: '6 letters', hint2: 'Plural', hint3: 'Like a seesaw' },
      { id: 'bio-2.5.w2', type: 'text', question: 'A turning force is called a ___ .', answer: 'moment', options: [], hint1: '6 letters', hint2: 'Starts with m', hint3: 'Calculated as F × d' },
      { id: 'bio-2.5.w3', type: 'text', question: 'Unit of moment? (two words abbreviated)', answer: 'N m', options: [], hint1: 'Newton metres', hint2: 'Capital N', hint3: 'Force × distance unit' },
      { id: 'bio-2.5.w4', type: 'choice', question: 'In the arm, what is the pivot?', options: ['Wrist', 'Elbow', 'Shoulder', 'Hand'], answer: 'Elbow', hint1: 'Forearm bends here', hint2: 'Joint', hint3: '5 letters' },
    ],
    level1: [
      { id: 'bio-2.5.1', type: 'number', question: 'A 4 N weight is 0.5 m from a pivot. Moment in N m?', answer: '2', options: [], hint1: 'F × d', hint2: '4 × 0.5', hint3: 'Single digit' },
      { id: 'bio-2.5.2', type: 'choice', question: 'A longer spanner makes a bolt easier to undo because…', options: [
        'Force is larger',
        'Distance from pivot is larger, so moment is larger',
        'The bolt becomes hot',
        'It looks better',
      ], answer: 'Distance from pivot is larger, so moment is larger', hint1: 'Same force', hint2: 'Bigger d', hint3: 'F × d' },
      { id: 'bio-2.5.3', type: 'number', question: 'Force 20 N applied 0.3 m from pivot. Moment?', answer: '6', options: [], hint1: '20 × 0.3', hint2: 'Single digit', hint3: 'Six' },
    ],
    level2: [
      { id: 'bio-2.5.4', type: 'number', question: 'A 10 N load is 40 cm from elbow. Biceps attaches 5 cm from elbow. Biceps force in N?', answer: '80', options: [], hint1: 'Moments balance', hint2: '10 × 0.4 = F × 0.05', hint3: '4 ÷ 0.05' },
      { id: 'bio-2.5.5', type: 'choice', question: 'For a lever to be in equilibrium…', options: [
        'Effort > load',
        'Clockwise moments must equal anticlockwise moments',
        'Load is at the pivot',
        'There is no force',
      ], answer: 'Clockwise moments must equal anticlockwise moments', hint1: 'Balanced moments', hint2: 'Both sides equal', hint3: 'Standard rule' },
    ],
    level3: [
      { id: 'bio-2.5.6', type: 'number', question: 'Convert 12.5 cm to metres.', answer: '0.125', options: [], hint1: 'Divide by 100', hint2: 'Decimal', hint3: 'Three decimal places' },
      { id: 'bio-2.5.7', type: 'choice', question: 'A 3rd-class lever (like the forearm) trades force for…', options: [
        'Mass and weight',
        'Speed and range of movement',
        'Bone density',
        'Energy use',
      ], answer: 'Speed and range of movement', hint1: 'Hand moves far', hint2: 'Muscle moves a little', hint3: 'Trade-off' },
    ],
  },
};

// =====================================================================
// 2.6  GAS EXCHANGE
// =====================================================================
const GAS_EXCHANGE_TOPIC = {
  id: 'bio-2.6',
  title: 'Gas Exchange',
  slug: 'gas-exchange',
  pageRef: 'Section 2 · Topic 6',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Why we breathe',
        body:
          'Every cell in your body needs oxygen for respiration and produces carbon dioxide as waste. Gas exchange is the process of swapping these gases between the air and the blood. It happens in tiny air sacs in the lungs called alveoli. There are around 300 million alveoli — together giving a surface area roughly the size of a tennis court.',
        check: {
          question: 'Where in the lungs does gas exchange take place?',
          options: ['Trachea', 'Bronchi', 'Alveoli', 'Diaphragm'],
          answer: 'Alveoli',
          explainer: 'Alveoli are tiny air sacs at the end of bronchioles where oxygen and CO₂ swap with the blood.',
        },
      },
      {
        heading: 'Adaptations of an alveolus',
        body:
          'An alveolus is brilliantly designed for fast gas exchange. (1) HUGE surface area — many tiny sacs. (2) Walls only ONE cell thick → short diffusion distance. (3) Surrounded by capillaries that are also one cell thick. (4) Always moist — gases dissolve before crossing. (5) Constantly ventilated — breathing keeps fresh air arriving.',
        check: {
          question: 'Which adaptation gives the SHORTEST diffusion distance?',
          options: ['Many alveoli', 'Walls one cell thick', 'A moist surface', 'Many capillaries'],
          answer: 'Walls one cell thick',
          explainer: 'Single-cell walls in both alveolus and capillary mean oxygen only has to cross two thin layers.',
        },
      },
      {
        heading: 'Direction of diffusion',
        body:
          'Gases move from high to low concentration — diffusion. Air in the alveolus is high in oxygen and low in CO₂. Blood arriving from the body is the opposite. So oxygen diffuses INTO the blood, and CO₂ diffuses OUT into the alveolus to be breathed out. The blood leaves richly oxygenated; the air leaves richer in CO₂.',
        check: {
          question: 'Oxygen diffuses…',
          options: ['Out of blood into the alveolus', 'Out of the alveolus into the blood', 'Across the diaphragm', 'In a circle'],
          answer: 'Out of the alveolus into the blood',
          explainer: 'Oxygen moves from high concentration (alveolus) to low concentration (blood).',
        },
      },
    ],
    keyPoints: [
      'Gas exchange = swapping oxygen and CO₂ in alveoli',
      'Alveoli adaptations: huge surface area, walls 1 cell thick, moist, capillaries close, ventilated',
      'Diffusion goes from high to low concentration',
      'Oxygen → blood; CO₂ → alveolus',
      '~300 million alveoli per lung; ~70 m² surface area',
    ],
    workedExamples: [
      {
        problem: 'Explain TWO adaptations of an alveolus that speed up gas exchange.',
        steps: [
          { step: 1, instruction: 'Pick adaptation 1', working: 'Walls only one cell thick' },
          { step: 2, instruction: 'Effect of adaptation 1', working: 'Short diffusion distance → faster gas movement' },
          { step: 3, instruction: 'Pick adaptation 2', working: 'Many alveoli (huge total surface area)' },
          { step: 4, instruction: 'Effect of adaptation 2', working: 'More space for diffusion to happen at the same time' },
        ],
        answer: 'Walls one cell thick → short diffusion distance. Huge surface area → more diffusion happens at once. Both make gas exchange quicker.',
        commonMistake: 'Naming features without linking them to function. Always say WHY.',
      },
    ],
    whyItWorks:
      'Diffusion is fastest when (a) the concentration gradient is steep, (b) the surface area is large and (c) the distance is short. Alveoli tick all three boxes — that\u2019s why we can take in 30 ml of oxygen per breath at rest, or 10× more during exercise.',
  },
  glossary: [
    { term: 'Gas exchange', definition: 'Swapping oxygen and carbon dioxide between air and blood.' },
    { term: 'Alveolus', definition: 'A tiny air sac in the lungs where gas exchange happens.' },
    { term: 'Bronchus', definition: 'A large airway that branches from the trachea into a lung.' },
    { term: 'Bronchiole', definition: 'A smaller airway that branches off a bronchus, ending in alveoli.' },
    { term: 'Diffusion', definition: 'Net movement of particles from high to low concentration.' },
    { term: 'Capillary', definition: 'Tiny blood vessel one cell thick — site of all exchange with tissues.' },
    { term: 'Surface area', definition: 'Total area available for diffusion — bigger means faster.' },
  ],
  examQuestion: {
    prompt:
      'Describe two adaptations of an alveolus that make gas exchange efficient, and explain how each helps. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Walls one cell thick — short diffusion distance',
      'Large surface area (many alveoli) — more diffusion happens at once',
      'Capillaries close to alveolus — short distance to blood',
      'Moist lining — gases dissolve before crossing',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.6.w1', type: 'text', question: 'The tiny air sacs where gas exchange happens are called ___ .', answer: 'alveoli', options: [], hint1: 'Plural', hint2: '7 letters', hint3: 'Singular: alveolus' },
      { id: 'bio-2.6.w2', type: 'text', question: 'Which gas diffuses INTO the blood?', answer: 'oxygen', options: [], hint1: '6 letters', hint2: 'Symbol O₂', hint3: 'Needed for respiration' },
      { id: 'bio-2.6.w3', type: 'text', question: 'Which gas diffuses OUT of the blood at alveoli?', answer: 'carbon dioxide', options: [], hint1: 'Two words', hint2: 'Symbol CO₂', hint3: 'Waste of respiration', altAnswers: ['co2'] },
      { id: 'bio-2.6.w4', type: 'choice', question: 'Diffusion moves particles from…', options: ['Low to high concentration', 'High to low concentration', 'Cold to hot', 'Top to bottom'], answer: 'High to low concentration', hint1: 'Down a gradient', hint2: 'Net movement', hint3: 'Like a perfume spreading' },
    ],
    level1: [
      { id: 'bio-2.6.1', type: 'choice', question: 'Why are alveoli walls only one cell thick?', options: [
        'To stop bacteria entering',
        'To minimise diffusion distance',
        'To increase pressure',
        'To make breathing harder',
      ], answer: 'To minimise diffusion distance', hint1: 'Faster diffusion', hint2: 'Short path', hint3: 'Fick\u2019s law' },
      { id: 'bio-2.6.2', type: 'choice', question: 'Approximate combined surface area of alveoli in adult lungs?', options: ['1 m²', '7 m²', '70 m²', '700 m²'], answer: '70 m²', hint1: 'Tennis court size', hint2: 'Two digits', hint3: 'Tens of m²' },
      { id: 'bio-2.6.3', type: 'text', question: 'Tiny blood vessels surrounding alveoli are ___ .', answer: 'capillaries', options: [], hint1: 'Single-cell wall', hint2: 'Plural', hint3: '11 letters' },
    ],
    level2: [
      { id: 'bio-2.6.4', type: 'choice', question: 'Why is the inside of an alveolus moist?', options: [
        'For lubrication only',
        'So gases can dissolve before diffusing',
        'To kill germs',
        'To insulate the lungs',
      ], answer: 'So gases can dissolve before diffusing', hint1: 'Diffusion in solution', hint2: 'Wet surface speeds gas movement', hint3: 'Aqueous environment' },
      { id: 'bio-2.6.5', type: 'choice', question: 'Which factor does NOT speed up diffusion?', options: [
        'Larger surface area',
        'Bigger concentration difference',
        'Shorter distance',
        'Lower temperature',
      ], answer: 'Lower temperature', hint1: 'Heat speeds particles up', hint2: 'Temperature increases speed', hint3: 'Cold slows things down' },
    ],
    level3: [
      { id: 'bio-2.6.6', type: 'choice', question: 'During exercise, breathing rate increases. Why does this help gas exchange?', options: [
        'It makes alveoli bigger',
        'It keeps a steep concentration gradient by replacing air faster',
        'It cools the lungs',
        'It removes water from blood',
      ], answer: 'It keeps a steep concentration gradient by replacing air faster', hint1: 'Ventilation', hint2: 'Fresh O₂, removes CO₂', hint3: 'Maintains gradient' },
      { id: 'bio-2.6.7', type: 'choice', question: 'A smoker\u2019s alveoli have lost surface area. What is the consequence?', options: [
        'Faster gas exchange',
        'Less oxygen entering the blood per breath',
        'No effect',
        'Larger heart',
      ], answer: 'Less oxygen entering the blood per breath', hint1: 'Smaller SA = slower exchange', hint2: 'Emphysema', hint3: 'Reduced lung function' },
    ],
  },
};

// =====================================================================
// 2.7  BREATHING
// =====================================================================
const BREATHING_TOPIC = {
  id: 'bio-2.7',
  title: 'Breathing',
  slug: 'breathing',
  pageRef: 'Section 2 · Topic 7',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Inhaling — pulling air IN',
        body:
          'Breathing in (inhalation) needs muscles. The diaphragm (a sheet of muscle below the lungs) contracts and flattens. The intercostal muscles between the ribs contract, pulling the ribcage UP and OUT. Together this INCREASES the volume of the chest cavity. Larger volume = lower pressure inside the lungs, so air rushes IN from the higher-pressure outside.',
        check: {
          question: 'When you breathe IN, the diaphragm…',
          options: ['Relaxes and rises', 'Contracts and flattens', 'Stays still', 'Disappears'],
          answer: 'Contracts and flattens',
          explainer: 'Diaphragm contraction makes more room in the chest, lowering pressure so air flows in.',
        },
      },
      {
        heading: 'Exhaling — pushing air OUT',
        body:
          'Breathing out (exhalation) is mostly passive. The diaphragm relaxes and curves up; the intercostal muscles relax and the ribcage drops down and in. Chest volume DECREASES, pressure inside the lungs increases above atmospheric pressure, and air is pushed OUT.',
        check: {
          question: 'During exhalation, lung volume…',
          options: ['Increases', 'Decreases', 'Stays the same', 'Doubles'],
          answer: 'Decreases',
          explainer: 'Smaller chest volume → higher internal pressure → air is forced out.',
        },
      },
      {
        heading: 'The path of air',
        body:
          'Air enters the nose (or mouth), travels down the trachea, splits into two bronchi (one per lung), branches into smaller bronchioles, and ends in millions of alveoli where gas exchange happens. The trachea is held open by C-shaped rings of cartilage. Tiny hairs (cilia) and a mucus layer trap dust and bacteria before they reach the alveoli.',
        check: {
          question: 'Which structure holds the trachea OPEN?',
          options: ['Diaphragm', 'Cartilage rings', 'Cilia', 'Capillaries'],
          answer: 'Cartilage rings',
          explainer: 'C-shaped cartilage rings keep the trachea open against pressure changes during breathing.',
        },
      },
    ],
    keyPoints: [
      'Inhalation: diaphragm contracts (flattens) + intercostals contract → volume up, pressure down → air in',
      'Exhalation: diaphragm relaxes (rises) + intercostals relax → volume down, pressure up → air out',
      'Path: nose/mouth → trachea → bronchi → bronchioles → alveoli',
      'Trachea held open by C-shaped cartilage rings',
      'Cilia + mucus trap dust and bacteria',
    ],
    workedExamples: [
      {
        problem: 'Explain why a sudden chest wound (pneumothorax) makes the lung on that side stop working.',
        steps: [
          { step: 1, instruction: 'Normal breathing relies on…', working: 'Pressure differences between chest and outside air' },
          { step: 2, instruction: 'A wound lets in…', working: 'Outside air, equalising pressure' },
          { step: 3, instruction: 'Without pressure difference…', working: 'Air can\u2019t be sucked in or pushed out' },
          { step: 4, instruction: 'Result', working: 'Lung collapses and gas exchange stops on that side' },
        ],
        answer: 'A pressure difference is needed to drive air in and out. A chest wound destroys it, so the lung collapses.',
        commonMistake: 'Saying the lung explodes — it actually collapses inwards as the air outside it loses its low-pressure container.',
      },
    ],
    whyItWorks:
      'Breathing works by pressure differences, not by the lungs sucking. Muscles change the size of the chest, the volume change changes the pressure, and air follows the pressure gradient. The lungs are passive bags — they simply inflate and deflate as the box around them changes shape.',
  },
  glossary: [
    { term: 'Inhalation', definition: 'Breathing in — air enters the lungs.' },
    { term: 'Exhalation', definition: 'Breathing out — air leaves the lungs.' },
    { term: 'Diaphragm', definition: 'Sheet of muscle below the lungs that flattens during inhalation.' },
    { term: 'Intercostal muscles', definition: 'Muscles between the ribs that lift the ribcage during inhalation.' },
    { term: 'Trachea', definition: 'The windpipe — the main airway from throat to bronchi.' },
    { term: 'Bronchus / bronchi', definition: 'The two main airways branching from the trachea into the lungs.' },
    { term: 'Cilia', definition: 'Microscopic hairs that sweep dust and mucus out of the airways.' },
    { term: 'Mucus', definition: 'Sticky liquid that traps dust and bacteria in the airways.' },
  ],
  examQuestion: {
    prompt:
      'Describe what happens to the diaphragm and intercostal muscles during inhalation, and explain why air flows INTO the lungs as a result. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Diaphragm contracts and flattens',
      'Intercostal muscles contract, raising the ribcage up and out',
      'Volume of chest cavity increases',
      'Pressure inside lungs falls below atmospheric pressure → air flows in',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.7.w1', type: 'text', question: 'The windpipe is also called the ___ .', answer: 'trachea', options: [], hint1: '7 letters', hint2: 'Starts with t', hint3: 'Holds C-shaped cartilage' },
      { id: 'bio-2.7.w2', type: 'text', question: 'Sheet of muscle below the lungs?', answer: 'diaphragm', options: [], hint1: '9 letters', hint2: 'Starts with d', hint3: 'Flattens to inhale' },
      { id: 'bio-2.7.w3', type: 'choice', question: 'When you breathe in, your ribs move…', options: ['Down and in', 'Up and out', 'Backwards', 'Stay still'], answer: 'Up and out', hint1: 'Increases chest volume', hint2: 'Three words', hint3: 'Opposite of exhale' },
      { id: 'bio-2.7.w4', type: 'choice', question: 'Tiny hairs that sweep mucus up the airways are…', options: ['Alveoli', 'Cilia', 'Bronchi', 'Capillaries'], answer: 'Cilia', hint1: 'Greek for eyelash', hint2: 'Damaged by smoking', hint3: '5 letters' },
    ],
    level1: [
      { id: 'bio-2.7.1', type: 'choice', question: 'Inhalation happens because lung pressure is…', options: [
        'Higher than atmospheric',
        'Lower than atmospheric',
        'Equal to atmospheric',
        'Zero',
      ], answer: 'Lower than atmospheric', hint1: 'Air goes from high to low', hint2: 'Volume up → pressure down', hint3: 'Gas law: Boyle' },
      { id: 'bio-2.7.2', type: 'choice', question: 'Which structures help trap dust before air reaches alveoli?', options: ['Capillaries and platelets', 'Cilia and mucus', 'Heart and aorta', 'Stomach and bile'], answer: 'Cilia and mucus', hint1: 'Two words', hint2: 'Hairs + sticky liquid', hint3: 'In airways' },
      { id: 'bio-2.7.3', type: 'text', question: 'Air goes: nose → trachea → ___ → bronchioles → alveoli.', answer: 'bronchi', options: [], hint1: 'Plural of bronchus', hint2: '7 letters', hint3: 'Two main airways' },
    ],
    level2: [
      { id: 'bio-2.7.4', type: 'choice', question: 'During exhalation the diaphragm…', options: [
        'Contracts and flattens',
        'Relaxes and curves up',
        'Disappears',
        'Stays still',
      ], answer: 'Relaxes and curves up', hint1: 'Opposite to inhalation', hint2: 'Reduces chest volume', hint3: 'Passive process' },
      { id: 'bio-2.7.5', type: 'choice', question: 'Why is the trachea reinforced with cartilage rings?', options: [
        'To produce mucus',
        'To stop it collapsing during low-pressure inhalation',
        'To carry blood',
        'For digestion',
      ], answer: 'To stop it collapsing during low-pressure inhalation', hint1: 'Pressure inside drops', hint2: 'Hold airway open', hint3: 'Structural support' },
    ],
    level3: [
      { id: 'bio-2.7.6', type: 'choice', question: 'Smoking damages cilia. What is the consequence?', options: [
        'Faster blood flow',
        'Mucus and dust collect in airways → cough and infection risk',
        'Bigger lungs',
        'No effect',
      ], answer: 'Mucus and dust collect in airways → cough and infection risk', hint1: 'No clearance', hint2: 'Smoker\u2019s cough', hint3: 'Increased infection' },
      { id: 'bio-2.7.7', type: 'choice', question: 'Why is breathing often called a "pressure pump"?', options: [
        'The heart pumps the lungs',
        'Volume changes drive pressure differences that move air in and out',
        'Lungs squeeze blood',
        'It uses no muscles',
      ], answer: 'Volume changes drive pressure differences that move air in and out', hint1: 'Boyle\u2019s law', hint2: 'P × V = constant', hint3: 'Mechanical model' },
    ],
  },
};

// =====================================================================
// 2.8  EXERCISE, ASTHMA & SMOKING
// =====================================================================
const EAS_TOPIC = {
  id: 'bio-2.8',
  title: 'Exercise, Asthma and Smoking',
  slug: 'exercise-asthma-smoking',
  pageRef: 'Section 2 · Topic 8',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What exercise does to the body',
        body:
          'During exercise, muscles need more oxygen and produce more CO₂. The body responds: heart rate goes up to pump more blood; breathing rate AND depth increase to take in more oxygen; blood vessels in the muscles widen (vasodilation) to deliver more blood. Regular exercise makes the heart, lungs and circulation stronger and more efficient.',
        check: {
          question: 'Which is NOT a direct response to exercise?',
          options: ['Faster heart rate', 'Deeper breathing', 'More mucus in airways', 'Wider blood vessels in muscles'],
          answer: 'More mucus in airways',
          explainer: 'Mucus production is a smoker / illness response — not normal exercise.',
        },
      },
      {
        heading: 'Asthma — narrowed airways',
        body:
          'Asthma is a long-term condition where the bronchioles become inflamed and tighten, often triggered by pollen, dust, exercise or stress. Airflow drops, leading to wheezing, coughing and shortness of breath. A reliever inhaler (usually blue) contains a drug that relaxes the bronchiole muscles, opening the airways quickly.',
        check: {
          question: 'A blue reliever inhaler works by…',
          options: [
            'Killing bacteria in the lungs',
            'Relaxing the bronchiole muscles to open airways',
            'Adding extra oxygen',
            'Making mucus thicker',
          ],
          answer: 'Relaxing the bronchiole muscles to open airways',
          explainer: 'The drug widens (dilates) the airways so air flows freely again.',
        },
      },
      {
        heading: 'Why smoking is so harmful',
        body:
          'Cigarette smoke contains tar, nicotine and carbon monoxide. Tar coats the alveoli and damages cilia, so dust and bacteria stay in the lungs (chronic cough, infections). Tar can also break down alveoli walls, reducing surface area for gas exchange — emphysema. Nicotine is highly addictive and raises blood pressure. Carbon monoxide bonds tightly to haemoglobin, blocking oxygen transport.',
        check: {
          question: 'Which chemical in cigarette smoke binds to haemoglobin and stops it carrying oxygen?',
          options: ['Tar', 'Nicotine', 'Carbon monoxide', 'Caffeine'],
          answer: 'Carbon monoxide',
          explainer: 'Carbon monoxide bonds 200× more strongly than oxygen, so red blood cells carry far less O₂.',
        },
      },
    ],
    keyPoints: [
      'Exercise → faster/deeper breathing, faster heart rate, vasodilation in muscles',
      'Long-term exercise = stronger heart and lungs',
      'Asthma = inflamed, narrowed bronchioles → wheezing/coughing',
      'Reliever (blue) inhaler relaxes bronchiole muscles',
      'Cigarette smoke: tar damages alveoli/cilia; nicotine = addictive; CO blocks O₂ transport',
    ],
    workedExamples: [
      {
        problem: 'A regular runner has resting heart rate 50 bpm; their friend (no exercise) has 75 bpm. Explain.',
        steps: [
          { step: 1, instruction: 'Identify the difference', working: 'Runner has lower resting HR' },
          { step: 2, instruction: 'Why?', working: 'Runner\u2019s heart is stronger and pumps more blood per beat (stroke volume up)' },
          { step: 3, instruction: 'So at rest…', working: 'Fewer beats are needed to meet oxygen demand' },
        ],
        answer: 'Regular exercise makes the heart muscle stronger; it pumps more blood per beat, so resting heart rate falls.',
        commonMistake: 'Saying the runner\u2019s heart is "smaller". It is actually larger and stronger.',
      },
    ],
    whyItWorks:
      'Healthy lungs and heart, and clear airways, are needed to keep oxygen delivery matching demand. Exercise upgrades the body\u2019s capacity. Asthma temporarily restricts airflow but is treatable. Smoking causes long-term, often permanent damage by destroying the very surfaces gas exchange depends on.',
  },
  glossary: [
    { term: 'Asthma', definition: 'A long-term lung condition where bronchioles narrow, restricting airflow.' },
    { term: 'Inhaler', definition: 'A device that delivers a drug into the lungs (e.g. relievers for asthma).' },
    { term: 'Tar', definition: 'A sticky chemical in cigarette smoke that damages alveoli and cilia.' },
    { term: 'Nicotine', definition: 'The addictive drug in tobacco that raises heart rate and blood pressure.' },
    { term: 'Carbon monoxide', definition: 'A poisonous gas in cigarette smoke that blocks oxygen transport.' },
    { term: 'Emphysema', definition: 'A lung disease where alveoli walls break down, reducing gas exchange.' },
    { term: 'Vasodilation', definition: 'Widening of blood vessels — increases blood flow.' },
  ],
  examQuestion: {
    prompt:
      'Describe two short-term and one long-term effect of regular exercise on the body. Then explain TWO ways cigarette smoke damages the lungs. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Short-term: heart rate increases / breathing deepens / vasodilation in muscles',
      'Long-term: stronger heart / lower resting heart rate / larger lung capacity',
      'Tar damages cilia and alveoli, reducing surface area for gas exchange',
      'Carbon monoxide bonds to haemoglobin, reducing oxygen delivery',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.8.w1', type: 'choice', question: 'During exercise, breathing rate…', options: ['Decreases', 'Stays the same', 'Increases', 'Stops'], answer: 'Increases', hint1: 'More O₂ needed', hint2: 'Up not down', hint3: '9 letters' },
      { id: 'bio-2.8.w2', type: 'text', question: 'A long-term lung condition where airways narrow is called ___ .', answer: 'asthma', options: [], hint1: 'Starts with a', hint2: '6 letters', hint3: 'Treated with inhalers' },
      { id: 'bio-2.8.w3', type: 'choice', question: 'Which chemical in cigarette smoke is addictive?', options: ['Tar', 'Nicotine', 'CO', 'Sugar'], answer: 'Nicotine', hint1: 'Drug-like effect', hint2: 'Starts with n', hint3: '8 letters' },
      { id: 'bio-2.8.w4', type: 'choice', question: 'Tar mainly damages…', options: ['Bones', 'Alveoli and cilia', 'Liver', 'Stomach'], answer: 'Alveoli and cilia', hint1: 'In the airways', hint2: 'Three words', hint3: 'Reduces gas exchange' },
    ],
    level1: [
      { id: 'bio-2.8.1', type: 'choice', question: 'Carbon monoxide is dangerous because it…', options: [
        'Makes you cough',
        'Bonds to haemoglobin and blocks oxygen transport',
        'Causes diabetes',
        'Speeds up digestion',
      ], answer: 'Bonds to haemoglobin and blocks oxygen transport', hint1: 'Affects red blood cells', hint2: 'Stops O₂ binding', hint3: 'CO + haemoglobin' },
      { id: 'bio-2.8.2', type: 'text', question: 'Inhalers used during an asthma attack relax muscles in the ___ .', answer: 'bronchioles', options: [], hint1: 'Smaller airways', hint2: 'Plural', hint3: '11 letters' },
      { id: 'bio-2.8.3', type: 'choice', question: 'A trained athlete\u2019s resting heart rate is usually…', options: ['Higher than average', 'Lower than average', 'The same', 'Zero'], answer: 'Lower than average', hint1: 'Stronger heart', hint2: 'Bigger stroke volume', hint3: 'Fewer beats needed' },
    ],
    level2: [
      { id: 'bio-2.8.4', type: 'choice', question: 'Emphysema is caused when…', options: [
        'Bronchioles become longer',
        'Alveoli walls break down, reducing surface area',
        'Capillaries grow larger',
        'Mucus thickens',
      ], answer: 'Alveoli walls break down, reducing surface area', hint1: 'Permanent damage', hint2: 'Smoking-related', hint3: 'Less SA = less gas exchange' },
      { id: 'bio-2.8.5', type: 'text', question: 'Which group of muscles widen in muscles to deliver more blood during exercise? (one word)', answer: 'arterioles', options: [], hint1: 'Small arteries', hint2: 'Vasodilation', hint3: '10 letters', altAnswers: ['arteries'] },
    ],
    level3: [
      { id: 'bio-2.8.6', type: 'choice', question: 'Why might an asthma sufferer find cold weather a trigger?', options: [
        'Cold air kills cilia',
        'Cold air can irritate sensitive airways and cause bronchioles to constrict',
        'Cold air freezes blood',
        'Cold air contains pollen',
      ], answer: 'Cold air can irritate sensitive airways and cause bronchioles to constrict', hint1: 'Trigger response', hint2: 'Airway irritation', hint3: 'Constriction reflex' },
      { id: 'bio-2.8.7', type: 'choice', question: 'A pregnant woman who smokes raises the risk of…', options: [
        'Her baby being too tall',
        'Low birth weight (CO blocks O₂ to the baby)',
        'Twins',
        'Albinism',
      ], answer: 'Low birth weight (CO blocks O₂ to the baby)', hint1: 'CO crosses placenta', hint2: 'Less O₂ to fetus', hint3: 'Baby grows slower' },
    ],
  },
};

// =====================================================================
// 2.9  HUMAN REPRODUCTIVE SYSTEM
// =====================================================================
const REPRO_TOPIC = {
  id: 'bio-2.9',
  title: 'Human Reproductive System',
  slug: 'reproductive-system',
  pageRef: 'Section 2 · Topic 9',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Two systems, two gametes',
        body:
          'Reproduction needs two specialised sex cells called gametes. The male gamete is the SPERM cell, made in the testes. The female gamete is the EGG cell (ovum), made in the ovaries. Sperm are tiny, mobile, and produced in millions; eggs are larger, stationary, and released roughly once every 28 days.',
        check: {
          question: 'Where are sperm cells made?',
          options: ['Penis', 'Testes', 'Bladder', 'Prostate'],
          answer: 'Testes',
          explainer: 'Sperm are produced inside the seminiferous tubules of the testes.',
        },
      },
      {
        heading: 'Adaptations of sperm and egg',
        body:
          'Sperm cells have a TAIL (flagellum) for swimming, packed mitochondria for energy, and an enzyme-filled head (acrosome) to break into the egg. Egg cells are large, contain food stores (yolk-like nutrients) for the early embryo, and have a jelly-like coating that hardens after fertilisation to stop further sperm.',
        check: {
          question: 'Why do sperm have many mitochondria?',
          options: [
            'For protection',
            'To release energy needed for swimming',
            'To break the egg surface',
            'To digest food',
          ],
          answer: 'To release energy needed for swimming',
          explainer: 'Mitochondria release energy via aerobic respiration; sperm need lots to swim a long way.',
        },
      },
      {
        heading: 'Fertilisation and pregnancy',
        body:
          'During intercourse, sperm enter the female reproductive tract and swim up through the cervix and uterus into the oviducts (fallopian tubes). If a sperm meets an egg, fertilisation can happen — their nuclei fuse to form a zygote with 46 chromosomes (23 from each parent). The zygote divides as it travels back to the uterus, where it implants in the lining and grows into an embryo.',
        check: {
          question: 'A zygote has how many chromosomes?',
          options: ['23', '46', '92', '12'],
          answer: '46',
          explainer: 'Sperm and egg each carry 23. Fertilisation joins them to make 46 — the normal human number.',
        },
      },
    ],
    keyPoints: [
      'Sperm = male gamete (in testes); egg/ovum = female gamete (in ovaries)',
      'Sperm: flagellum tail, lots of mitochondria, acrosome enzymes',
      'Egg: large, food stores, jelly coating that hardens after fertilisation',
      'Fertilisation: nuclei of sperm + egg fuse → zygote (46 chromosomes)',
      'Zygote → embryo, implants in the uterus lining',
    ],
    workedExamples: [
      {
        problem: 'List THREE adaptations of a sperm cell and explain how each helps fertilisation.',
        steps: [
          { step: 1, instruction: 'Adaptation 1', working: 'Tail (flagellum) — swims to egg' },
          { step: 2, instruction: 'Adaptation 2', working: 'Many mitochondria — release energy for swimming' },
          { step: 3, instruction: 'Adaptation 3', working: 'Acrosome — enzymes digest the egg coat to allow entry' },
        ],
        answer: 'Tail for movement; mitochondria for energy; acrosome enzymes to enter the egg.',
        commonMistake: 'Confusing mitochondria with the nucleus. Mitochondria release energy; the nucleus carries DNA.',
      },
    ],
    whyItWorks:
      'Each gamete is built for its job. Sperm need to FIND an egg — so they\u2019re tiny, fast and energy-loaded. Eggs need to FEED an early embryo — so they\u2019re larger and stocked with nutrients. Fertilisation combines half the genes from each parent, giving every new human a unique mix of inherited features.',
  },
  glossary: [
    { term: 'Gamete', definition: 'A specialised sex cell — sperm or egg.' },
    { term: 'Sperm', definition: 'The male gamete; tiny and motile, made in the testes.' },
    { term: 'Egg / ovum', definition: 'The female gamete; large and packed with nutrients, made in the ovaries.' },
    { term: 'Testis (testes)', definition: 'The male organ that produces sperm and the hormone testosterone.' },
    { term: 'Ovary', definition: 'The female organ that releases an egg each month and makes oestrogen/progesterone.' },
    { term: 'Oviduct (fallopian tube)', definition: 'The tube where fertilisation usually takes place.' },
    { term: 'Uterus (womb)', definition: 'The muscular organ where a fertilised egg implants and grows into a baby.' },
    { term: 'Zygote', definition: 'The single cell formed when sperm and egg fuse; has 46 chromosomes.' },
  ],
  examQuestion: {
    prompt:
      'Describe TWO adaptations of a sperm cell and TWO adaptations of an egg cell. Explain how each adaptation helps reproduction. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Sperm: tail / flagellum for swimming towards the egg',
      'Sperm: many mitochondria for energy (or acrosome with enzymes to enter the egg)',
      'Egg: large size with food stores for the early embryo',
      'Egg: jelly coat that hardens after fertilisation to stop further sperm entering',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.9.w1', type: 'text', question: 'Male gamete?', answer: 'sperm', options: [], hint1: '5 letters', hint2: 'Tiny and motile', hint3: 'Symbol used: sperm cell' },
      { id: 'bio-2.9.w2', type: 'text', question: 'Female gamete?', answer: 'egg', options: [], hint1: '3 letters', hint2: 'Also called ovum', hint3: 'Released from ovary', altAnswers: ['ovum'] },
      { id: 'bio-2.9.w3', type: 'text', question: 'Sperm are made in the ___ .', answer: 'testes', options: [], hint1: 'Plural', hint2: '6 letters', hint3: 'Two of these in males' },
      { id: 'bio-2.9.w4', type: 'text', question: 'Eggs are made in the ___ .', answer: 'ovaries', options: [], hint1: 'Plural', hint2: '7 letters', hint3: 'Two of these in females' },
    ],
    level1: [
      { id: 'bio-2.9.1', type: 'choice', question: 'Where does fertilisation usually happen?', options: ['Uterus', 'Vagina', 'Oviduct', 'Cervix'], answer: 'Oviduct', hint1: 'Also called fallopian tube', hint2: '7 letters', hint3: 'Tube between ovary and uterus' },
      { id: 'bio-2.9.2', type: 'number', question: 'Number of chromosomes in a human zygote?', answer: '46', options: [], hint1: '23 from each parent', hint2: 'Two digits', hint3: 'Even number' },
      { id: 'bio-2.9.3', type: 'choice', question: 'The fertilised egg implants in the…', options: ['Ovary', 'Uterus lining', 'Oviduct', 'Cervix'], answer: 'Uterus lining', hint1: 'Where the baby grows', hint2: 'Two words', hint3: 'Endometrium' },
    ],
    level2: [
      { id: 'bio-2.9.4', type: 'choice', question: 'The acrosome at the head of a sperm contains…', options: [
        'Food stores',
        'Enzymes to digest the egg coat',
        'Oxygen',
        'Mucus',
      ], answer: 'Enzymes to digest the egg coat', hint1: 'Allows entry into egg', hint2: 'Like keys', hint3: 'Used at fertilisation' },
      { id: 'bio-2.9.5', type: 'choice', question: 'Why does the egg coat harden after fertilisation?', options: [
        'To kill bacteria',
        'To stop further sperm entering',
        'To produce hormones',
        'To carry oxygen',
      ], answer: 'To stop further sperm entering', hint1: 'Prevents polyspermy', hint2: 'Cortical reaction', hint3: 'One sperm only' },
    ],
    level3: [
      { id: 'bio-2.9.6', type: 'choice', question: 'Why do sperm contain very little cytoplasm?', options: [
        'They don\u2019t need nutrients',
        'To stay light and swim faster',
        'To produce hormones',
        'To carry oxygen',
      ], answer: 'To stay light and swim faster', hint1: 'Streamlined design', hint2: 'No food store needed', hint3: 'Speed advantage' },
      { id: 'bio-2.9.7', type: 'text', question: 'Sperm + egg fuse to make a single cell called a ___ .', answer: 'zygote', options: [], hint1: 'Starts with z', hint2: '6 letters', hint3: 'Has 46 chromosomes' },
    ],
  },
};

// =====================================================================
// 2.10  THE MENSTRUAL CYCLE
// =====================================================================
const MENSTRUAL_TOPIC = {
  id: 'bio-2.10',
  title: 'The Menstrual Cycle',
  slug: 'menstrual-cycle',
  pageRef: 'Section 2 · Topic 10',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'A 28-day cycle',
        body:
          'The menstrual cycle is the body\u2019s monthly preparation for a possible pregnancy. It lasts on average 28 days. There are four key events: (1) MENSTRUATION (days 1-5) — the uterus lining breaks down. (2) The lining REBUILDS (days 6-13). (3) OVULATION (about day 14) — an egg is released. (4) The lining is MAINTAINED (days 15-28) waiting for a fertilised egg.',
        check: {
          question: 'On which day of a typical 28-day cycle is an egg released?',
          options: ['Day 1', 'Day 7', 'Day 14', 'Day 28'],
          answer: 'Day 14',
          explainer: 'Ovulation is roughly halfway through the cycle, around day 14.',
        },
      },
      {
        heading: 'The four hormones (KS3 level)',
        body:
          'Four hormones drive the cycle. FSH (from the pituitary) stimulates an egg to develop in the ovary. OESTROGEN (from the ovary) builds up the uterus lining. LH (from the pituitary) triggers ovulation. PROGESTERONE (from the empty follicle) maintains the lining. If pregnancy doesn\u2019t happen, progesterone drops and menstruation begins again.',
        check: {
          question: 'Which hormone causes ovulation?',
          options: ['FSH', 'LH', 'Oestrogen', 'Progesterone'],
          answer: 'LH',
          explainer: 'A surge of LH from the pituitary causes the ovary to release the egg.',
        },
      },
      {
        heading: 'Pregnancy and the cycle',
        body:
          'If a sperm fertilises the egg, the embryo implants in the thick uterus lining and produces hormones (notably hCG) that keep progesterone HIGH. The lining is maintained, so menstruation does not happen — a missed period is often the first sign of pregnancy. If no fertilisation occurs, progesterone falls, the lining sheds, and the cycle restarts.',
        check: {
          question: 'A missed period can be an early sign of pregnancy because…',
          options: [
            'The body has no eggs left',
            'Progesterone stays high so the lining is maintained',
            'FSH stops being made',
            'Ovulation happens twice',
          ],
          answer: 'Progesterone stays high so the lining is maintained',
          explainer: 'High progesterone (sustained by hCG) keeps the lining thick and prevents bleeding.',
        },
      },
    ],
    keyPoints: [
      'Cycle length ≈ 28 days, ovulation around day 14',
      'FSH → egg matures',
      'Oestrogen → builds uterus lining',
      'LH → triggers ovulation',
      'Progesterone → maintains the lining',
      'No fertilisation → progesterone falls → menstruation',
    ],
    workedExamples: [
      {
        problem: 'A woman has a 32-day cycle. Estimate the day of ovulation.',
        steps: [
          { step: 1, instruction: 'Ovulation typically happens 14 days BEFORE the next period', working: '32 - 14 = 18' },
          { step: 2, instruction: 'So in a 32-day cycle…', working: 'Ovulation is around day 18' },
        ],
        answer: 'Around day 18 of the cycle.',
        commonMistake: 'Just adding 14 to day 1. Ovulation is 14 days before the NEXT period, not 14 days into the current cycle.',
      },
    ],
    whyItWorks:
      'The menstrual cycle is a beautifully timed loop of feedback. FSH and LH from the brain control the ovary; oestrogen and progesterone from the ovary feed back to the brain. The system "rolls the dice" each month: prepare, release, maintain, reset. It only stops if pregnancy occurs (or at menopause).',
  },
  glossary: [
    { term: 'Menstrual cycle', definition: 'The roughly 28-day cycle that prepares the female body for possible pregnancy.' },
    { term: 'Menstruation', definition: 'The breakdown and shedding of the uterus lining (period).' },
    { term: 'Ovulation', definition: 'The release of an egg from the ovary, around day 14.' },
    { term: 'FSH', definition: 'Follicle Stimulating Hormone — from the pituitary; causes an egg to mature.' },
    { term: 'LH', definition: 'Luteinising Hormone — from the pituitary; triggers ovulation.' },
    { term: 'Oestrogen', definition: 'Female hormone from the ovary; builds the uterus lining.' },
    { term: 'Progesterone', definition: 'Hormone from the ovary that maintains the uterus lining.' },
    { term: 'Pituitary gland', definition: 'A small gland at the base of the brain that releases FSH and LH.' },
  ],
  examQuestion: {
    prompt:
      'Name the four hormones in the menstrual cycle and describe what each does. Then explain why menstruation does NOT occur during pregnancy. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'FSH → matures the egg in the ovary',
      'Oestrogen → builds up the uterus lining',
      'LH → triggers ovulation; Progesterone → maintains the lining',
      'During pregnancy, progesterone stays high (supported by hCG), so the lining is maintained and no menstruation',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.10.w1', type: 'number', question: 'Average length of menstrual cycle in days?', answer: '28', options: [], hint1: 'Roughly four weeks', hint2: 'Two digits', hint3: 'Lunar month-ish' },
      { id: 'bio-2.10.w2', type: 'text', question: 'Release of an egg from an ovary is called ___ .', answer: 'ovulation', options: [], hint1: 'Starts with o', hint2: '9 letters', hint3: 'Around day 14' },
      { id: 'bio-2.10.w3', type: 'text', question: 'Period bleeding (lining breakdown) is called ___ .', answer: 'menstruation', options: [], hint1: 'Starts with m', hint2: '12 letters', hint3: 'Days 1-5' },
      { id: 'bio-2.10.w4', type: 'choice', question: 'FSH is made in the…', options: ['Ovary', 'Pituitary', 'Uterus', 'Liver'], answer: 'Pituitary', hint1: 'Brain gland', hint2: 'Starts with p', hint3: '9 letters' },
    ],
    level1: [
      { id: 'bio-2.10.1', type: 'choice', question: 'Which hormone builds up the uterus lining?', options: ['FSH', 'LH', 'Oestrogen', 'Progesterone'], answer: 'Oestrogen', hint1: 'From ovary', hint2: 'Starts with o', hint3: '9 letters' },
      { id: 'bio-2.10.2', type: 'choice', question: 'Which hormone maintains the lining once built?', options: ['FSH', 'LH', 'Oestrogen', 'Progesterone'], answer: 'Progesterone', hint1: 'Pro = forwards (toward pregnancy)', hint2: 'Starts with p', hint3: '12 letters' },
      { id: 'bio-2.10.3', type: 'choice', question: 'The trigger for ovulation is a SURGE of…', options: ['FSH', 'LH', 'Oestrogen', 'Progesterone'], answer: 'LH', hint1: 'Two letters', hint2: 'Luteinising hormone', hint3: 'Mid-cycle spike' },
    ],
    level2: [
      { id: 'bio-2.10.4', type: 'number', question: 'On a 30-day cycle, around what day is ovulation? (think 14 before next period)', answer: '16', options: [], hint1: '30 - 14', hint2: 'Two digits', hint3: 'Day 16' },
      { id: 'bio-2.10.5', type: 'choice', question: 'If progesterone falls sharply…', options: [
        'The lining is maintained',
        'The lining breaks down → menstruation',
        'The egg is released',
        'No effect',
      ], answer: 'The lining breaks down → menstruation', hint1: 'No support', hint2: 'Period starts', hint3: 'Drop = breakdown' },
    ],
    level3: [
      { id: 'bio-2.10.6', type: 'choice', question: 'Why is hCG important in early pregnancy?', options: [
        'It causes ovulation',
        'It keeps progesterone high so the lining is maintained',
        'It builds the embryo\u2019s skeleton',
        'It thins the uterus lining',
      ], answer: 'It keeps progesterone high so the lining is maintained', hint1: 'From the embryo', hint2: 'Detected by pregnancy tests', hint3: 'Maintains corpus luteum' },
      { id: 'bio-2.10.7', type: 'text', question: 'After menopause, the cycle stops because the ___ no longer release eggs.', answer: 'ovaries', options: [], hint1: 'Plural', hint2: '7 letters', hint3: 'Egg-producing organs' },
    ],
  },
};

// =====================================================================
// 2.11  HEALTH AND PREGNANCY
// =====================================================================
const PREGNANCY_TOPIC = {
  id: 'bio-2.11',
  title: 'Health and Pregnancy',
  slug: 'health-and-pregnancy',
  pageRef: 'Section 2 · Topic 11',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Nine months from cell to baby',
        body:
          'A pregnancy lasts about 40 weeks (~9 months). The fertilised egg (zygote) divides into a ball of cells called the embryo, which implants in the uterus lining. From about week 8 it is called a foetus and develops recognisable organs. The placenta forms in the uterus wall — it is the lifeline that swaps gases, nutrients and waste between mother and baby.',
        check: {
          question: 'After roughly how many weeks of pregnancy is the embryo called a foetus?',
          options: ['1', '4', '8', '20'],
          answer: '8',
          explainer: 'After about week 8 the embryo is called a foetus and has the basic body plan formed.',
        },
      },
      {
        heading: 'The placenta and umbilical cord',
        body:
          'The placenta is a disc-shaped organ attached to the uterus wall. It allows OXYGEN and DISSOLVED FOOD (glucose, amino acids) to pass from mother\u2019s blood to baby\u2019s blood, and CARBON DIOXIDE and other WASTES to pass back. The two blood supplies stay separate — substances move only by diffusion. The umbilical cord connects placenta to baby and contains arteries and a vein.',
        check: {
          question: 'Which substance does NOT cross the placenta from mother to baby?',
          options: ['Oxygen', 'Glucose', 'Red blood cells', 'Amino acids'],
          answer: 'Red blood cells',
          explainer: 'The mother\u2019s and baby\u2019s blood stay separate — only dissolved substances pass.',
        },
      },
      {
        heading: 'Lifestyle and pregnancy',
        body:
          'What the mother does affects the baby. Smoking exposes the baby to nicotine and CARBON MONOXIDE — reducing oxygen supply and risking low birth weight. Alcohol crosses the placenta and can damage development (foetal alcohol syndrome). Some drugs and infections (like rubella) can cause birth defects. A balanced diet with plenty of folic acid lowers the risk of spinal defects.',
        check: {
          question: 'Why is smoking during pregnancy harmful?',
          options: [
            'It changes the mother\u2019s mood',
            'CO blocks oxygen transport in the mother\u2019s blood, reducing oxygen to the baby',
            'It makes the baby grow faster',
            'It lowers the mother\u2019s temperature',
          ],
          answer: 'CO blocks oxygen transport in the mother\u2019s blood, reducing oxygen to the baby',
          explainer: 'Carbon monoxide bonds to haemoglobin so less oxygen reaches the baby — cause of low birth weight.',
        },
      },
    ],
    keyPoints: [
      'Pregnancy ≈ 40 weeks',
      'Zygote → embryo → foetus (≈ week 8)',
      'Placenta exchanges O₂, food and waste between mother and baby',
      'Umbilical cord links baby to placenta',
      'Smoking, alcohol, some drugs and infections can harm the baby',
      'Folic acid lowers spinal defect risk',
    ],
    workedExamples: [
      {
        problem: 'Explain why a pregnant smoker is more likely to have a baby with low birth weight.',
        steps: [
          { step: 1, instruction: 'Carbon monoxide binds to…', working: 'Haemoglobin in mother\u2019s blood' },
          { step: 2, instruction: 'Effect on mother\u2019s blood', working: 'Less oxygen carried' },
          { step: 3, instruction: 'Effect on baby', working: 'Less oxygen crosses the placenta to the baby' },
          { step: 4, instruction: 'Result', working: 'Baby grows more slowly → lower birth weight' },
        ],
        answer: 'Carbon monoxide reduces oxygen transport in the mother, less reaches the baby through the placenta, growth slows.',
        commonMistake: 'Saying nicotine is the main culprit. Nicotine is addictive but CO is what reduces oxygen delivery to the baby.',
      },
    ],
    whyItWorks:
      'The placenta is a tiny diffusion factory: it has a huge surface area and very thin walls so molecules cross quickly without mixing the two blood supplies. Anything that interferes with this exchange — narrowing the mother\u2019s blood supply, lowering her oxygen, or introducing toxins — directly affects the developing baby.',
  },
  glossary: [
    { term: 'Embryo', definition: 'A fertilised egg in the first 8 weeks of development.' },
    { term: 'Foetus', definition: 'The developing baby from about week 8 onwards (US: fetus).' },
    { term: 'Placenta', definition: 'The organ in the uterus wall where mother and baby exchange substances.' },
    { term: 'Umbilical cord', definition: 'The link between baby and placenta, containing two arteries and a vein.' },
    { term: 'Implantation', definition: 'When the embryo attaches to the uterus lining.' },
    { term: 'Folic acid', definition: 'A B vitamin needed in early pregnancy to prevent spinal defects.' },
    { term: 'Foetal alcohol syndrome', definition: 'A pattern of birth defects caused by drinking during pregnancy.' },
  ],
  examQuestion: {
    prompt:
      'Describe the role of the placenta during pregnancy. Then explain ONE way a mother\u2019s lifestyle choices can harm the baby. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Placenta exchanges O₂ and dissolved nutrients (glucose, amino acids) from mother to baby',
      'Placenta also removes waste (CO₂, urea) from baby to mother',
      'Mother\u2019s and baby\u2019s blood stay separate — diffusion only',
      'Smoking → CO blocks O₂ transport → low birth weight (or alcohol → foetal alcohol syndrome)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.11.w1', type: 'number', question: 'Pregnancy lasts about how many weeks?', answer: '40', options: [], hint1: '~9 months', hint2: 'Two digits', hint3: 'Multiples of 4 ≈ 40' },
      { id: 'bio-2.11.w2', type: 'text', question: 'Disc-shaped organ that links mother and baby is the ___ .', answer: 'placenta', options: [], hint1: 'Starts with p', hint2: '8 letters', hint3: 'In the uterus wall' },
      { id: 'bio-2.11.w3', type: 'text', question: 'The cord between baby and placenta is the ___ ___ .', answer: 'umbilical cord', options: [], hint1: 'Two words', hint2: 'Cuts the navel', hint3: 'Belly button is left over' },
      { id: 'bio-2.11.w4', type: 'choice', question: 'Folic acid lowers the risk of…', options: ['Diabetes', 'Spinal defects in baby', 'High blood pressure', 'Cancer'], answer: 'Spinal defects in baby', hint1: 'Neural tube defects', hint2: 'Three words', hint3: 'B-vitamin' },
    ],
    level1: [
      { id: 'bio-2.11.1', type: 'choice', question: 'Which substance does NOT cross the placenta?', options: ['Oxygen', 'Glucose', 'Red blood cells', 'Amino acids'], answer: 'Red blood cells', hint1: 'Bloods stay separate', hint2: 'Big cells', hint3: 'Three words' },
      { id: 'bio-2.11.2', type: 'choice', question: 'When is the embryo called a foetus?', options: ['Week 1', 'Week 4', 'Week 8', 'Week 30'], answer: 'Week 8', hint1: 'Roughly 2 months', hint2: 'Single digit', hint3: '8' },
      { id: 'bio-2.11.3', type: 'choice', question: 'Alcohol during pregnancy can cause…', options: ['Improved growth', 'Foetal alcohol syndrome', 'Stronger bones', 'Better hearing'], answer: 'Foetal alcohol syndrome', hint1: 'Three words', hint2: 'FAS', hint3: 'Birth defects' },
    ],
    level2: [
      { id: 'bio-2.11.4', type: 'choice', question: 'Why must the placenta have a large surface area?', options: [
        'To support the baby\u2019s weight',
        'To allow fast exchange of gases and nutrients',
        'To produce blood',
        'To absorb light',
      ], answer: 'To allow fast exchange of gases and nutrients', hint1: 'Diffusion', hint2: 'More SA = faster swap', hint3: 'Like alveoli' },
      { id: 'bio-2.11.5', type: 'choice', question: 'Why do mother\u2019s and baby\u2019s blood stay separate?', options: [
        'They are the same temperature',
        'To prevent immune attack and to control exchange',
        'For colour reasons',
        'They flow in the same direction',
      ], answer: 'To prevent immune attack and to control exchange', hint1: 'Different blood groups', hint2: 'Filter via diffusion', hint3: 'Immune protection' },
    ],
    level3: [
      { id: 'bio-2.11.6', type: 'text', question: 'Mother and baby blood stay separate; substances pass by ___ .', answer: 'diffusion', options: [], hint1: 'High to low concentration', hint2: '9 letters', hint3: 'Passive process' },
      { id: 'bio-2.11.7', type: 'choice', question: 'The viral infection rubella is dangerous in early pregnancy because it can cause…', options: [
        'Faster growth',
        'Birth defects (e.g. deafness, heart problems)',
        'Higher blood pressure in mother only',
        'No effect',
      ], answer: 'Birth defects (e.g. deafness, heart problems)', hint1: 'Crosses placenta', hint2: 'Most risk in first trimester', hint3: 'Vaccinated MMR' },
    ],
  },
};

// =====================================================================
// 2.12  DRUGS
// =====================================================================
const DRUGS_TOPIC = {
  id: 'bio-2.12',
  title: 'Drugs',
  slug: 'drugs',
  pageRef: 'Section 2 · Topic 12',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is a drug?',
        body:
          'A drug is any chemical that changes the way the body works. Drugs can be MEDICINAL (used to treat illness, e.g. antibiotics, painkillers) or RECREATIONAL (used for pleasure or social reasons, e.g. caffeine, alcohol, nicotine). Some recreational drugs are legal (caffeine, alcohol over 18, nicotine) while others are illegal (cannabis, cocaine, heroin).',
        check: {
          question: 'Caffeine is what kind of drug?',
          options: ['Medicinal only', 'A legal recreational drug (mild stimulant)', 'A heart medicine', 'An antibiotic'],
          answer: 'A legal recreational drug (mild stimulant)',
          explainer: 'Caffeine in tea, coffee and some fizzy drinks is a mild legal stimulant.',
        },
      },
      {
        heading: 'How drugs affect the body',
        body:
          'Drugs change how nerves and the brain work. STIMULANTS (caffeine, nicotine, cocaine) speed up brain activity, raising heart rate and alertness. DEPRESSANTS (alcohol, sleeping pills) slow brain activity — reaction times get longer, judgement weakens. PAINKILLERS (paracetamol, codeine) block pain signals to the brain. ANTIBIOTICS (e.g. penicillin) kill bacteria but DO NOT affect viruses.',
        check: {
          question: 'Which type of drug slows reaction times and impairs judgement?',
          options: ['Stimulant', 'Depressant', 'Antibiotic', 'Vitamin'],
          answer: 'Depressant',
          explainer: 'Alcohol is the classic example. It depresses brain activity, slowing reactions.',
        },
      },
      {
        heading: 'Addiction, tolerance and withdrawal',
        body:
          'Many drugs are addictive — the user develops a strong urge to take more. With repeated use, TOLERANCE builds: the same dose has a smaller effect, so users take more, increasing risk. If someone stops, WITHDRAWAL symptoms (anxiety, shaking, cravings) push them back. Long-term effects can include liver damage (alcohol), lung disease (smoking) and heart attacks (cocaine).',
        check: {
          question: 'Tolerance means…',
          options: [
            'The drug stops working completely',
            'The same dose has a smaller effect, so users take more',
            'The drug is now illegal',
            'The drug becomes safer',
          ],
          answer: 'The same dose has a smaller effect, so users take more',
          explainer: 'Tolerance pushes users towards higher doses — increasing health risks.',
        },
      },
    ],
    keyPoints: [
      'Drug = any chemical that changes how the body works',
      'Medicinal vs recreational; legal vs illegal',
      'Stimulants speed brain up; depressants slow it down',
      'Painkillers block pain signals; antibiotics kill bacteria (not viruses)',
      'Addiction: tolerance + withdrawal',
      'Long-term effects: liver, lung, heart and brain damage',
    ],
    workedExamples: [
      {
        problem: 'A friend has a runny nose and asks for antibiotics. Explain why this is a bad idea.',
        steps: [
          { step: 1, instruction: 'Identify the cause of a typical runny nose', working: 'Usually a virus (cold/flu)' },
          { step: 2, instruction: 'How antibiotics work', working: 'They kill bacteria, not viruses' },
          { step: 3, instruction: 'Effect of unnecessary use', working: 'Bacteria become resistant — antibiotic won\u2019t work next time it is needed' },
        ],
        answer: 'Antibiotics don\u2019t work on viruses, so they\u2019d have no effect AND overuse promotes antibiotic resistance.',
        commonMistake: 'Thinking antibiotics work on every infection. They don\u2019t work on viruses.',
      },
    ],
    whyItWorks:
      'Most drugs work by changing the way nerve cells communicate. Stimulants make signals travel more easily; depressants block them. Painkillers stop pain signals before they reach the brain. Knowing the type helps explain side-effects: alcohol slows reactions because it is a depressant, not because of "lack of focus".',
  },
  glossary: [
    { term: 'Drug', definition: 'Any chemical that changes the way the body works.' },
    { term: 'Medicinal drug', definition: 'A drug used to treat or prevent illness (e.g. antibiotics, painkillers).' },
    { term: 'Recreational drug', definition: 'A drug taken for pleasure or social reasons.' },
    { term: 'Stimulant', definition: 'A drug that speeds up brain activity (e.g. caffeine, nicotine).' },
    { term: 'Depressant', definition: 'A drug that slows brain activity (e.g. alcohol).' },
    { term: 'Painkiller', definition: 'A drug that blocks pain signals (e.g. paracetamol).' },
    { term: 'Antibiotic', definition: 'A drug that kills bacteria — does not work on viruses.' },
    { term: 'Tolerance', definition: 'When the body adapts so the same dose has a smaller effect.' },
    { term: 'Withdrawal', definition: 'Unpleasant symptoms when stopping a drug after dependence has formed.' },
  ],
  examQuestion: {
    prompt:
      'Explain the difference between a stimulant and a depressant, giving ONE example of each. Why is taking antibiotics for a viral cold a bad idea? (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Stimulant: speeds brain activity, e.g. caffeine / nicotine / cocaine',
      'Depressant: slows brain activity, e.g. alcohol / sleeping pills',
      'Antibiotics kill bacteria but don\u2019t work on viruses',
      'Overuse causes resistance, making antibiotics less effective in future',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.12.w1', type: 'choice', question: 'Caffeine is a…', options: ['Painkiller', 'Stimulant', 'Depressant', 'Antibiotic'], answer: 'Stimulant', hint1: 'Speeds you up', hint2: '9 letters', hint3: 'Found in tea/coffee' },
      { id: 'bio-2.12.w2', type: 'choice', question: 'Alcohol is a…', options: ['Stimulant', 'Depressant', 'Antibiotic', 'Painkiller'], answer: 'Depressant', hint1: 'Slows reactions', hint2: '10 letters', hint3: 'Opposite of stimulant' },
      { id: 'bio-2.12.w3', type: 'choice', question: 'Antibiotics kill…', options: ['Viruses', 'Bacteria', 'Fungi only', 'Everything'], answer: 'Bacteria', hint1: 'Not viruses', hint2: '8 letters', hint3: 'Penicillin example' },
      { id: 'bio-2.12.w4', type: 'text', question: 'Cigarette smoke contains the addictive chemical ___ .', answer: 'nicotine', options: [], hint1: '8 letters', hint2: 'Stimulant', hint3: 'Starts with n' },
    ],
    level1: [
      { id: 'bio-2.12.1', type: 'choice', question: 'A drug used to treat illness is called…', options: ['Recreational', 'Medicinal', 'Toxic', 'Mineral'], answer: 'Medicinal', hint1: '9 letters', hint2: 'Starts with m', hint3: 'Treats disease' },
      { id: 'bio-2.12.2', type: 'choice', question: 'Why do antibiotics NOT work on a cold?', options: [
        'Colds are not real',
        'Antibiotics only kill bacteria, but colds are caused by viruses',
        'Antibiotics are too weak',
        'Doctors haven\u2019t invented one yet',
      ], answer: 'Antibiotics only kill bacteria, but colds are caused by viruses', hint1: 'Different organisms', hint2: 'Cold = virus', hint3: 'Antibiotic = bacteria-killer' },
      { id: 'bio-2.12.3', type: 'text', question: 'Repeated drug use leads to ___ , where the same dose has a smaller effect.', answer: 'tolerance', options: [], hint1: 'Starts with t', hint2: '9 letters', hint3: 'Body adapts' },
    ],
    level2: [
      { id: 'bio-2.12.4', type: 'choice', question: 'Alcohol can damage which organ over the long term?', options: ['Bones', 'Liver', 'Skin', 'Pancreas'], answer: 'Liver', hint1: 'Detoxifies blood', hint2: '5 letters', hint3: 'Cirrhosis' },
      { id: 'bio-2.12.5', type: 'choice', question: 'A user who suddenly stops a drug they\u2019re addicted to may suffer…', options: [
        'Improved alertness only',
        'Withdrawal symptoms — anxiety, shakes, cravings',
        'Instant recovery',
        'Higher tolerance',
      ], answer: 'Withdrawal symptoms — anxiety, shakes, cravings', hint1: 'Physical & mental', hint2: 'Comes with addiction', hint3: 'Why people relapse' },
    ],
    level3: [
      { id: 'bio-2.12.6', type: 'choice', question: 'Why is overuse of antibiotics a public-health problem?', options: [
        'They run out',
        'Bacteria evolve resistance, making future infections harder to treat',
        'They become illegal',
        'They make food less tasty',
      ], answer: 'Bacteria evolve resistance, making future infections harder to treat', hint1: 'Natural selection of resistant strains', hint2: 'AMR', hint3: 'Why finishing the course matters' },
      { id: 'bio-2.12.7', type: 'choice', question: 'Why are stimulants like cocaine particularly dangerous to the heart?', options: [
        'They cool the heart',
        'They speed up heart rate and raise blood pressure, risking heart attack',
        'They lower oxygen in food',
        'They strengthen heart valves',
      ], answer: 'They speed up heart rate and raise blood pressure, risking heart attack', hint1: 'Stimulant effect', hint2: 'Cardiovascular risk', hint3: 'Especially in young users' },
    ],
  },
};

// =====================================================================
// 2.2  DIGESTION (existing live topic, retained)
// =====================================================================
const DIGESTION_TOPIC = {
  id: 'bio-2.2',
  title: 'Digestion',
  slug: 'digestion',
  pageRef: 'Section 2 · Topic 2',
  difficulty: 'foundation',
  diagram: 'digestive-system',
  learn: {
    chunks: [
      {
        heading: 'Why we digest food',
        body:
          'The food we eat is made of huge molecules — starch, proteins, fats — that are too big to pass through the wall of the intestine into the blood. Digestion uses enzymes to break these big molecules into small, soluble ones (sugars, amino acids, fatty acids + glycerol) that can be absorbed into the bloodstream and used by the body.',
        check: {
          question: 'Why must food be digested?',
          options: ['To make it taste nicer', 'So it can dissolve in water and pass into the blood', 'To kill all bacteria', 'To keep food warm'],
          answer: 'So it can dissolve in water and pass into the blood',
          explainer: 'Large molecules cannot be absorbed. Digestion makes them small and soluble.',
        },
      },
      {
        heading: 'The three key enzymes',
        body:
          'Carbohydrase (specifically amylase) breaks starch into simple sugars. Protease breaks proteins into amino acids. Lipase breaks lipids (fats) into fatty acids and glycerol. Each enzyme is specific — it only works on one type of substrate, fitting like a lock and key.',
        check: {
          question: 'Which enzyme breaks down proteins?',
          options: ['Amylase', 'Lipase', 'Protease', 'Catalase'],
          answer: 'Protease',
          explainer: 'Protease breaks proteins into amino acids. Hint: the name matches the substrate.',
        },
      },
      {
        heading: 'Bile and absorption',
        body:
          'Bile is made in the liver and stored in the gall bladder. It is alkaline, neutralising the acid arriving from the stomach, and it emulsifies fats into tiny droplets so lipase can break them down faster. Digested food is then absorbed in the small intestine through finger-like villi; the large intestine takes back water.',
        check: {
          question: 'Bile does NOT…',
          options: ['Neutralise stomach acid', 'Emulsify fats into tiny droplets', 'Digest protein itself', 'Increase the surface area of fats'],
          answer: 'Digest protein itself',
          explainer: 'Bile is not an enzyme — it just helps digestion by changing conditions.',
        },
      },
    ],
    keyPoints: [
      'Amylase (carbohydrase) → starch into simple sugars',
      'Protease → proteins into amino acids',
      'Lipase → lipids into fatty acids + glycerol',
      'Bile is made in the liver, stored in the gall bladder',
      'Bile neutralises stomach acid AND emulsifies fats',
      'Small intestine absorbs food; large intestine takes back water',
    ],
    workedExamples: [
      {
        problem: 'A student tests food X with Benedict\u2019s solution and it turns brick red. What does this show?',
        steps: [
          { step: 1, instruction: 'Benedict\u2019s tests for…', working: 'Reducing sugars' },
          { step: 2, instruction: 'A positive result is…', working: 'Brick red' },
          { step: 3, instruction: 'Conclude', working: 'Food X contains reducing sugar' },
        ],
        answer: 'Food X contains reducing sugar (Benedict\u2019s turns brick red when positive).',
        commonMistake: 'Writing "contains sugar". Benedict\u2019s tests only for REDUCING sugars.',
      },
      {
        problem: 'Why is the small intestine folded internally with tiny villi?',
        steps: [
          { step: 1, instruction: 'Identify the function', working: 'Absorb food into the blood' },
          { step: 2, instruction: 'What helps absorption?', working: 'Large surface area' },
          { step: 3, instruction: 'How?', working: 'Folds + villi + microvilli increase surface area' },
        ],
        answer: 'Folds, villi and microvilli all increase the surface area of the small intestine so food is absorbed more quickly.',
        commonMistake: 'Only mentioning villi. A full answer needs length + folds + villi.',
      },
    ],
    whyItWorks:
      'Enzymes have a shape (active site) that fits one specific substrate — lock and key. If the temperature or pH is wrong, the shape changes and the enzyme stops working.',
  },
  glossary: [
    { term: 'Enzyme', definition: 'A protein catalyst that speeds up a specific reaction in living things.' },
    { term: 'Substrate', definition: 'The molecule an enzyme acts on.' },
    { term: 'Amylase', definition: 'A carbohydrase that breaks starch into sugar.' },
    { term: 'Protease', definition: 'An enzyme that breaks proteins into amino acids.' },
    { term: 'Lipase', definition: 'An enzyme that breaks fats into fatty acids and glycerol.' },
    { term: 'Bile', definition: 'Alkaline liquid from the liver that neutralises acid and emulsifies fat.' },
    { term: 'Emulsify', definition: 'Split a fat droplet into many smaller droplets (bigger surface area).' },
    { term: 'Villi', definition: 'Finger-like folds in the small intestine that increase surface area.' },
  ],
  requiredPractical: {
    title: 'Required Practical: Food tests',
    aqaRef: 'KS3 Practical — Food Tests',
    aim: 'Identify starch, sugar, protein and fat in different food samples.',
    method: [
      'Starch — add iodine. A blue-black colour shows starch is present.',
      'Sugar (reducing) — add Benedict\u2019s and heat in a water bath. Brick-red means reducing sugar.',
      'Protein — add biuret at room temperature. Purple/lilac shows protein.',
      'Fat — shake food with ethanol, then add water. A cloudy white emulsion shows fat.',
    ],
    safety: 'Wear eye protection. Do NOT heat Benedict\u2019s directly over a flame — use a water bath. Ethanol is flammable.',
    tips: [
      'Record the STARTING colour of each test before adding food.',
      'Use a white tile under tubes so colour changes are clear.',
      'Run a control with just water to compare fairly.',
    ],
  },
  examQuestion: {
    prompt:
      'A student tests a food sample and finds it contains reducing sugar and protein, but no starch or fat. Describe the tests and their positive/negative results. (6 marks)',
    maxMarks: 6,
    markSchemePoints: [
      'Benedict\u2019s + heat → brick-red precipitate = reducing sugar present',
      'Biuret at room temperature → purple = protein present',
      'Iodine stayed orange/brown (not blue-black) → no starch',
      'Ethanol + water stayed clear (no cloudy emulsion) → no fat',
      'State that the test is specific / compare starting colour vs change',
      'Correct use of control / safety point for a mark',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-2.2.w1', type: 'text', question: 'Which enzyme digests starch?', answer: 'amylase', hint1: 'Carbohydrase', hint2: 'Made in saliva', hint3: '7 letters' },
      { id: 'bio-2.2.w2', type: 'text', question: 'Which enzyme digests proteins?', answer: 'protease', hint1: 'The name matches', hint2: 'Breaks proteins into amino acids', hint3: '8 letters' },
      { id: 'bio-2.2.w3', type: 'text', question: 'Which enzyme digests fats?', answer: 'lipase', hint1: 'Works on lipids', hint2: 'Fatty acids + glycerol', hint3: '6 letters' },
      { id: 'bio-2.2.w4', type: 'choice', question: 'Bile is stored in the…', options: ['Liver', 'Gall bladder', 'Stomach', 'Small intestine'], answer: 'Gall bladder', hint1: 'Not where it is made', hint2: 'Small green sac', hint3: 'Two words' },
    ],
    level1: [
      { id: 'bio-2.2.1', type: 'choice', question: 'Starch is broken down into…', options: ['Amino acids', 'Fatty acids', 'Glucose', 'Glycerol'], answer: 'Glucose', hint1: 'A simple sugar', hint2: 'Used in respiration', hint3: '7 letters' },
      { id: 'bio-2.2.2', type: 'text', question: 'Which food test uses Benedict\u2019s solution?', answer: 'sugar', hint1: 'Brick red positive', hint2: 'Reducing ___', hint3: '5 letters', altAnswers: ['reducing sugar', 'sugars'] },
      { id: 'bio-2.2.3', type: 'choice', question: 'Which reagent turns purple when protein is present?', options: ['Iodine', 'Benedict\u2019s', 'Biuret', 'Ethanol'], answer: 'Biuret', hint1: 'Starts with B', hint2: 'Not heated', hint3: '6 letters' },
      { id: 'bio-2.2.4', type: 'choice', question: 'What colour does iodine go when starch is present?', options: ['Purple', 'Blue-black', 'Brick red', 'Cloudy white'], answer: 'Blue-black', hint1: 'Dark colour', hint2: 'Hyphenated', hint3: 'Inky' },
    ],
    level2: [
      { id: 'bio-2.2.5', type: 'choice', question: 'Why does bile speed up fat digestion?', options: ['Digests fat directly', 'Emulsifies fat → larger surface area for lipase', 'Raises body temperature', 'Denatures protease'], answer: 'Emulsifies fat → larger surface area for lipase', hint1: 'Bile is not an enzyme', hint2: 'Larger SA = faster', hint3: 'Emulsification' },
      { id: 'bio-2.2.6', type: 'text', question: 'What pH does protease in the stomach work best at? (number)', answer: '2', hint1: 'Stomach is acidic', hint2: 'Very low pH', hint3: 'Single digit' },
      { id: 'bio-2.2.7', type: 'choice', question: 'What pH does bile create in the small intestine?', options: ['Very acidic', 'Neutral to alkaline', 'Extremely alkaline', 'No change'], answer: 'Neutral to alkaline', hint1: 'Opposite of stomach', hint2: 'Suitable for small-intestine enzymes', hint3: 'pH 7–9' },
      { id: 'bio-2.2.8', type: 'text', question: 'Name ONE organ that makes all three digestive enzymes.', answer: 'pancreas', hint1: 'Behind the stomach', hint2: 'Also makes insulin', hint3: '8 letters' },
    ],
    level3: [
      { id: 'bio-2.2.9', type: 'choice', question: 'An enzyme heated to 70 °C stops working. Why?', options: ['Runs out of substrate', 'Active site changes shape → no longer fits', 'Substrate evaporates', 'Turns into bile'], answer: 'Active site changes shape → no longer fits', hint1: 'Denaturing', hint2: 'Shape is key', hint3: 'Lock and key' },
      { id: 'bio-2.2.10', type: 'text', question: 'The lining of the small intestine has finger-like folds called ___.', answer: 'villi', hint1: 'Plural of villus', hint2: 'Increase surface area', hint3: '5 letters' },
      { id: 'bio-2.2.11', type: 'choice', question: 'Which features give the small intestine a huge surface area?', options: ['Length', 'Villi', 'Microvilli', 'All of the above'], answer: 'All of the above', hint1: 'Multiple features stack', hint2: 'Length + villi + microvilli', hint3: 'Combined' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  NUTRITION_TOPIC,
  DIGESTION_TOPIC,
  SKELETON_TOPIC,
  MUSCULAR_TOPIC,
  FORCE_TOPIC,
  GAS_EXCHANGE_TOPIC,
  BREATHING_TOPIC,
  EAS_TOPIC,
  REPRO_TOPIC,
  MENSTRUAL_TOPIC,
  PREGNANCY_TOPIC,
  DRUGS_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const humansAsOrganismsTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
